import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Spacer,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Tooltip,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from '@chakra-ui/react';
import { 
  DownloadIcon,
  DeleteIcon,
  AddIcon,
  CheckCircleIcon,
  UnlockIcon,
  SearchIcon,
  CloseIcon,
} from '@chakra-ui/icons';
import { FiSend, FiMapPin } from 'react-icons/fi';
import { ChevronLeft, Play, Check, CircleCheck, Sparkles, ShieldCheck } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useCustomFields from '../../hooks/useCustomFields';
import useTableData from '../../hooks/useTableData';
import useAdvisorCompliance from '../../hooks/useAdvisorCompliance';
import useRowSelection from '../../hooks/useRowSelection';
import useTableOperations from '../../hooks/useTableOperations';
import useCellEditing from '../../hooks/useCellEditing';
import useEmailOrchestration from '../../hooks/useEmailOrchestration';
import useSSEStream from '../../hooks/useSSEStream';
import useContactEnrichment from '../../hooks/useContactEnrichment';
import useContactAddress from '../../hooks/useContactAddress';
import useSmartleadIntegration from '../../hooks/useSmartleadIntegration';
import useHeyreachIntegration from '../../hooks/useHeyreachIntegration';
import { useCampaignComplianceStatus } from '../../hooks/useCampaignComplianceStatus';
import { useInsufficientCredits } from '../../contexts/InsufficientCreditsContext';
import { useAuth } from '../../contexts/AuthContext';
import useCreditWarning from '../../hooks/useCreditWarning';
import CreditWarningModal from '../common/CreditWarningModal';
import { 
  getCompanyColumns, 
  getContactColumns,
  getCompanyTableMinWidth,
  getContactTableMinWidth,
  getMessageColumnsWidth,
  calculateRowHeight,
  ROW_TYPES 
} from '../../utils/tableUtils';
import TableRow from './table/TableRow';
import CompanyTableHeader from './table/CompanyTableHeader';
import ContactTableHeader from './table/ContactTableHeader';
import CustomFieldPreviewBanner from './table/CustomFieldPreviewBanner';
import AdvisorComplianceSummaryStrip from './compliance/AdvisorComplianceSummaryStrip';
import AdvisorNonCompliantModal from './compliance/AdvisorNonCompliantModal';
import TableContainer from './table/TableContainer';
import CustomFieldGenerationModal from './CustomFieldGenerationModal';
import EmailDraftModal from './EmailDraftModal';
import SmartleadExportModal from './SmartleadExportModal';
import HeyreachExportModal from './HeyreachExportModal';
import ComplianceReviewModal from './table/ComplianceReviewModal';
import CompanyDetailView from './CompanyDetailView';
import ContactDetailView from './ContactDetailView';
import api from '../../services/api';
import { campaignService } from '../../services/campaignService';
import { COMPLIANCE_FILTERS, matchesComplianceFilter } from '../../utils/complianceFilters';
import { computeComplianceContentHash } from '../../utils/complianceHash';

/** Table approve/reject/continue should only hijack the chat credit gate when config and operation kind match. */
function chatCreditGateMatchesSession(gate, configId, operationType) {
  if (!gate?.gate_id || String(gate.config_id) !== String(configId)) return false;
  if (gate.operation_type && operationType && gate.operation_type !== operationType) return false;
  return true;
}

const VirtualizedPersonaResults = ({ 
  campaignId,
  isProcessing = false, 
  onStreamingDataUpdate = null,
  onCampaignComplete = null,
  onRefreshReady = null,
  shouldFetchInitialData = false,
  onCustomFieldGenerate = null,
  onCustomFieldApprove = null,
  onEmailStatusUpdate = null,
  onResearchStatusUpdate = null,
  isStatusBarVisible = false,
  messageDrafts = [],
  exportTracking = [],
  onExportTrackingRefresh = null,
  onMessageDraftEdit = null,
  onMessageDraftRename = null,
  onMessageDraftDelete = null,
  onMessageDraftDescriptionUpdate = null,
  onHasDataChange = null,
  researchStopRef = null,
  chatCreditGateSession = null,
  onSubmitChatCreditGate = null,
}) => {
  const [activeTab, setActiveTab] = useState(0); // 0 for companies, 1 for contacts
  const [isSelectingAllRows, setIsSelectingAllRows] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null); // Company detail view
  const [selectedContact, setSelectedContact] = useState(null); // Contact detail view
  const [researchFields, setResearchFields] = useState([]); // Fields from CompanyDetailView for "Run for All"
  const [isRunModalOpen, setIsRunModalOpen] = useState(false);
  const [runConfigSelections, setRunConfigSelections] = useState({}); // config_id → boolean

  // Contact research fields (mirrors company research fields for contacts tab)
  const [contactResearchFields, setContactResearchFields] = useState([]);
  const [isContactRunModalOpen, setIsContactRunModalOpen] = useState(false);
  const [contactRunConfigSelections, setContactRunConfigSelections] = useState({});
  const [containerHeight, setContainerHeight] = useState(400);
  const [listHeight, setListHeight] = useState(400);
  const [, setContainerWidth] = useState(0);
  const [collapsedExportGroupIds, setCollapsedExportGroupIds] = useState(() => new Set());
  const [generatingDraftId, setGeneratingDraftId] = useState(null);
  const [isGeneratingCustomFields, setIsGeneratingCustomFields] = useState(false);
  // True when an in-progress run-for-all is detected via the observer handshake
  // (i.e. after a page refresh while a job was still running). Cleared when the
  // job reaches 100 %. Combined with isBulkResearching (live SSE) for button guards.
  const [isResearchRunning, setIsResearchRunning] = useState(false);
  const customFieldGenerationInProgressRef = useRef(false);
  const customFieldCompletionTimerRef = useRef(null);
  const contactPreviewTypeRef = useRef(null); // 'custom_field' | 'email_draft' | null
  
  // Track completion state to trigger single resize after streaming finishes
  const [customFieldCompletionCounter, setCustomFieldCompletionCounter] = useState(0);
  
  const navigate = useNavigate();
  const listRef = useRef(null);
  const containerRef = useRef(null);
  const widthMeasureRef = useRef(null); // Dedicated ref for width measurement
  const companiesOuterContainerRef = useRef(null);
  const contactsOuterContainerRef = useRef(null);
  const toast = useToast();
  const { user } = useAuth();
  const isAnonymous = user?.is_anonymous === true;
  const { showInsufficientCreditsModal } = useInsufficientCredits();
  const { complianceStatus, isApproved: isCampaignApproved } = useCampaignComplianceStatus(campaignId);
  const { 
    withCreditWarning, 
    isWarningOpen, 
    warningData, 
    handleConfirm, 
    handleOptOut, 
    closeWarning,
    userSubscriptionStatus
  } = useCreditWarning();
  const queryClient = useQueryClient();

  // Custom fields hooks
  const companyFields = useCustomFields(campaignId, 'companies');
  const contactFields = useCustomFields(campaignId, 'contacts');
  
  // Table data hook - pass listRef for scroll control
  const {
    companiesData,
    contactsData,
    totalCompanies,
    totalContacts,
    isLoadingTabData,
    hasMoreCompanies,
    hasMoreContacts,
    activeVersionId,
    setCompaniesData,
    setContactsData,
    setTotalCompanies,
    setTotalContacts,
    fetchTabData,
    handleStreamingUpdate,
    loadMoreData,
    removeContactRow,
    companiesOffsetRef,
    contactsOffsetRef,
    currentSearchRef,
    complianceFieldMap,
    hasComplianceReviewers,
    getUnfilteredTotalContacts,
  } = useTableData(campaignId, companyFields, contactFields, listRef);
  
  // Notify parent when campaign entity counts change
  useEffect(() => {
    onHasDataChange?.(totalCompanies > 0 || totalContacts > 0);
  }, [totalCompanies, totalContacts, onHasDataChange]);

  // Enrich raw data with custom field values so navigation passes enriched contacts (with notes, status, etc.)
  const enrichedCompanies = useMemo(() => {
    const enriched = companyFields.enrichEntityData(companiesData);
    return enriched;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companiesData, companyFields.enrichEntityData, companyFields.fieldDefinitions.length, companyFields.fieldValuesVersion]);

  const enrichedContacts = useMemo(() => {
    const enriched = contactFields.enrichEntityData(contactsData);
    return enriched;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsData, contactFields.enrichEntityData, contactFields.fieldDefinitions.length, contactFields.fieldValuesVersion]);

  // Derived indices for detail view navigation arrows
  const selectedCompanyIndex = selectedCompany
    ? enrichedCompanies.findIndex(c => c.elasticsearch_id === selectedCompany.elasticsearch_id)
    : -1;
  const selectedContactIndex = selectedContact
    ? enrichedContacts.findIndex(c => c.elasticsearch_id === selectedContact.elasticsearch_id)
    : -1;

  // Pending nav refs: when we're at the last loaded item and fetch more, navigate to this index once data arrives
  const pendingCompanyNavRef = useRef(null);
  const pendingContactNavRef = useRef(null);

  useEffect(() => {
    if (pendingCompanyNavRef.current !== null && enrichedCompanies.length > pendingCompanyNavRef.current) {
      setSelectedCompany(enrichedCompanies[pendingCompanyNavRef.current]);
      pendingCompanyNavRef.current = null;
    }
  }, [enrichedCompanies]);

  useEffect(() => {
    if (pendingContactNavRef.current !== null && enrichedContacts.length > pendingContactNavRef.current) {
      setSelectedContact(enrichedContacts[pendingContactNavRef.current]);
      pendingContactNavRef.current = null;
    }
  }, [enrichedContacts]);

  const handleCompanyPrev = selectedCompanyIndex > 0
    ? () => setSelectedCompany(enrichedCompanies[selectedCompanyIndex - 1])
    : null;

  const handleCompanyNext = (() => {
    if (selectedCompanyIndex < 0) return null;
    const nextIdx = selectedCompanyIndex + 1;
    if (nextIdx < enrichedCompanies.length) return () => setSelectedCompany(enrichedCompanies[nextIdx]);
    if (hasMoreCompanies) return () => {
      pendingCompanyNavRef.current = nextIdx;
      fetchTabData(0, companiesOffsetRef.current, true, currentSearchRef.current);
    };
    return null;
  })();

  const handleContactPrev = selectedContactIndex > 0
    ? () => setSelectedContact(enrichedContacts[selectedContactIndex - 1])
    : null;

  const handleContactNext = (() => {
    if (selectedContactIndex < 0) return null;
    const nextIdx = selectedContactIndex + 1;
    if (nextIdx < enrichedContacts.length) return () => setSelectedContact(enrichedContacts[nextIdx]);
    if (hasMoreContacts) return () => {
      pendingContactNavRef.current = nextIdx;
      fetchTabData(1, contactsOffsetRef.current, true, currentSearchRef.current);
    };
    return null;
  })();

  // Compute hasMoreData based on active tab
  const hasMoreData = activeTab === 0 ? hasMoreCompanies : hasMoreContacts;
  
  // Row selection hook
  const {
    selectedRows,
    handleRowSelect,
    handleSelectAll,
    clearSelection,
    setSelectedRowsFromIds,
  } = useRowSelection(activeTab === 0 ? companiesData : contactsData);
  
  // Plan B Pattern A2 (Linear-style hard filter) — bridge ref so the
  // early `useCellEditing` handler can call the surgical-remove helper
  // that's declared further down (after `advisorCompliance` /
  // `allMessageDrafts`). Standard "useEvent shim" pattern: declare the
  // ref now, point it at the real implementation in an effect.
  const surgicallyRemoveRowAfterEditRef = useRef(null);

  // Cell editing hook
  const {
    editingCell,
    editingCellValue,
    setEditingCell,
    setEditingCellValue,
  } = useCellEditing(async (entityId, fieldId, newValue, entityType) => {
    // Handle cell edit save
    const targetData = entityType === 'companies' ? companiesData : contactsData;
    const fieldHook = entityType === 'companies' ? companyFields : contactFields;
    
    const entity = targetData.find(e => e.id === entityId);
    if (!entity || !entity.membership_id) return;
    
    const fieldDef = fieldHook.fieldDefinitions.find(f => f.field_id === fieldId);
    if (!fieldDef) return;
    
    try {
      await fieldHook.updateFieldValue(entity.membership_id, fieldId, newValue);

      // Plan A — any contact field edit can change the per-contact
      // (subject, body) hash for at least one draft, which flips
      // previously-reviewed cells stale. Invalidating unconditionally
      // on contact edits is safe (single ~30ms aggregation) and
      // prevents missing a stale-flip when the edited field happens
      // to be a draft body.
      if (entityType === 'contacts' && campaignId) {
        queryClient.invalidateQueries({ queryKey: ['complianceSummary', campaignId] });
        // Plan B Pattern A2 — if the edit took this row out of the
        // active chip filter, vanish it from the table immediately.
        // No-op when the filter is "All" or compliance is disabled.
        await surgicallyRemoveRowAfterEditRef.current?.({
          contact: entity,
          editedFieldId: fieldId,
          newValue,
        });
      }

      toast({
        title: 'Field Updated',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Update Failed',
        description: error.response?.data?.detail || 'Failed to update field',
        status: 'error',
        duration: 3000,
      });
    }
  });
  
  // Table operations hook - pass listRef for scroll control
  const {
    isExporting,
    handleDeleteSelected,
    handleExport,
  } = useTableOperations(
    campaignId,
    activeVersionId,
    (entityType, updateFn, countDelta) => {
      // Handle data updates from operations
      if (entityType === 'companies') {
        setCompaniesData(updateFn);
        if (countDelta !== 0) {
          setTotalCompanies(prev => Math.max(0, prev + countDelta));
        }
      } else if (entityType === 'contacts') {
        setContactsData(updateFn);
        if (countDelta !== 0) {
          setTotalContacts(prev => Math.max(0, prev + countDelta));
        }
      }
    },
    withCreditWarning,
    // Refresh callback for operations that modify backend state (export, enrichment, etc.)
    async () => {
      await fetchTabData(activeTab, 0, false);
    },
    listRef  // Pass listRef for scroll control
  );
  
  // Modal states
  const { isOpen: isNoCreditsModalOpen, onOpen: onNoCreditsModalOpen, onClose: onNoCreditsModalClose } = useDisclosure();
  const { isOpen: isCustomFieldModalOpen, onOpen: onCustomFieldModalOpen, onClose: onCustomFieldModalClose } = useDisclosure();
  const { isOpen: isEmailDraftModalOpen, onClose: onEmailDraftModalClose } = useDisclosure();
  const { isOpen: isSmartleadExportModalOpen, onOpen: onSmartleadExportModalOpen, onClose: onSmartleadExportModalClose } = useDisclosure();
  const { isOpen: isHeyreachExportModalOpen, onOpen: onHeyreachExportModalOpen, onClose: onHeyreachExportModalClose } = useDisclosure();
  const { isOpen: isComplianceReviewModalOpen, onOpen: onComplianceReviewModalOpen, onClose: onComplianceReviewModalClose } = useDisclosure();

  /** Table / header paths that call approveEmailDrafts must sync MessageDraftingPanel + chat status bar. */
  const kickstartEmailDraftFanoutUi = useCallback(
    (configId) => {
      if (!configId) return;
      onEmailStatusUpdate?.(
        {
          completion_percentage: '1',
          message: 'Applying draft to contacts…',
          operation_id: 'email_draft_generation',
        },
        'email_drafts',
      );
      window.dispatchEvent(
        new CustomEvent('emailDraftFanoutStarted', { detail: { configId: String(configId) } }),
      );
    },
    [onEmailStatusUpdate],
  );

  // Email orchestration hook (pass withCreditWarning down)
  const emailOrchestration = useEmailOrchestration({
    campaignId,
    versionId: activeVersionId,
    withCreditWarning,
    onStatusUpdate: (status, type) => {
      // Forward email status updates to parent (CampaignChatInterface)
      if (onEmailStatusUpdate) {
        onEmailStatusUpdate(status, type);
      }
    },
    onDataUpdate: (update) => {
      // Forward to streaming update handler (same as custom fields)
      handleStreamingUpdate(update, activeTab);
    },
    onComplete: async (operationType) => {
      // Clear generation flag to allow banner to show
      customFieldGenerationInProgressRef.current = false;
      setIsGeneratingCustomFields(false);
      
      // Clear preview state on successful completion
      if (activeTab === 0) {
        companyFields.clearPreviewState();
      } else {
        contactFields.clearPreviewState();
      }
      
      // Refetch contact field definitions to detect preview state
      if (contactFields.refetchDefinitions) {
        await contactFields.refetchDefinitions();
      }
      
      // Refresh data after verification or drafting completes
      await fetchTabData(activeTab, 0, false);

      // Notify parent to refresh messageDrafts so newly approved email columns appear
      if (operationType === 'approval' || operationType === 'preview') {
        window.dispatchEvent(new Event('emailDraftApproved'));
        // Refresh header credit balance after the charge
        window.dispatchEvent(new Event('emailDraftCompleted'));
      }
    },
    onError: (error) => {
      // Clear generation flag on error too
      customFieldGenerationInProgressRef.current = false;
      setIsGeneratingCustomFields(false);
      
      // Handle insufficient credits error (402) - show modal instead of toast
      if (error.status === 402) {
        showInsufficientCreditsModal(error.detail ?? error.message ?? 'You do not have enough credits for this email operation.');
      } else {
        toast({
          title: 'Email Operation Failed',
          description: error.message || 'An error occurred',
          status: 'error',
          duration: 5000,
        });
      }
    }
  }); 

  // ── Research "Run for All" SSE stream ──────────────────────────────────────
  // Keep onResearchStatusUpdate in a ref so callbacks are stable (not recreated
  // on every render) while always calling the latest version of the prop.
  const onResearchStatusUpdateRef = useRef(onResearchStatusUpdate);
  useEffect(() => {
    onResearchStatusUpdateRef.current = onResearchStatusUpdate;
  }, [onResearchStatusUpdate]);

  const handleResearchStatus = useCallback((status) => {
    onResearchStatusUpdateRef.current?.(status, 'custom_fields');
  }, []);

  const handleResearchComplete = useCallback(async () => {
    toast({
      title: 'Research complete',
      description: 'Fields were researched and added to your table columns',
      status: 'success',
      duration: 4000,
      isClosable: true,
    });
    window.dispatchEvent(new Event('customFieldCompleted'));
    await Promise.all([
      companyFields.refetchDefinitions?.(),
      contactFields.refetchDefinitions?.(),
      fetchTabData(activeTab, 0, false),
    ]);
  }, [toast, companyFields, contactFields, fetchTabData, activeTab]);

  const handleResearchError = useCallback((error) => {
    toast({
      title: 'Research failed',
      description: error?.message || 'Something went wrong. Please try again.',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    // Clear any in-flight status bar
    onResearchStatusUpdateRef.current?.(null, null);
  }, [toast]);

  const {
    isStreaming: isBulkResearching,
    startStream: startResearchStream,
    stopStream: stopResearchStream,
  } = useSSEStream({
    onStatus: handleResearchStatus,
    onComplete: handleResearchComplete,
    onError: handleResearchError,
  });

  // Expose stopResearchStream to the parent (CampaignChatInterface) so that
  // handleStop can abort this SSE alongside the chat stream and custom-field SSE.
  useEffect(() => {
    if (researchStopRef) {
      researchStopRef.current = stopResearchStream;
    }
  }, [researchStopRef, stopResearchStream]);
  // ────────────────────────────────────────────────────────────────────────────

  // Contact enrichment hook (pass withCreditWarning and listRef down)
  const contactEnrichment = useContactEnrichment(
    campaignId,
    activeVersionId,
    async () => {
      // Refresh contacts data after enrichment
      await fetchTabData(activeTab, 0, false);
    },
    withCreditWarning,
    listRef  // Pass listRef for scroll control
  );

  // Contact address hook (on-demand Versium postal address reveal)
  const contactAddress = useContactAddress(
    campaignId,
    activeVersionId,
    async () => {
      await fetchTabData(activeTab, 0, false);
    },
    withCreditWarning,
    listRef
  );

  // Smartlead integration status hook
  const smartleadIntegration = useSmartleadIntegration();
  
  // HeyReach integration status hook
  const heyreachIntegration = useHeyreachIntegration();

  const scrollToTopSafely = useCallback(() => {
    listRef.current?.scrollToItem?.(0, 'start');
  }, []);

  // Calculate column configurations with fixed widths (no dynamic resizing)
  const effectiveFieldCount = companyFields.fieldDefinitions.length;

  const COMPANY_COLUMNS = useMemo(() => {
    return getCompanyColumns(effectiveFieldCount);
  }, [effectiveFieldCount]);

  const COMPANY_TABLE_MIN_WIDTH = useMemo(() => {
    return getCompanyTableMinWidth(effectiveFieldCount);
  }, [effectiveFieldCount]);
  
  // config_ids that already have a completed entry in messageDrafts.
  // Once a draft is completed it moves into the messageDrafts columns
  // ("Email Draft N: Subject/Body"), so we exclude its field defs from the
  // Build a Set of config_ids for all approved (completed) message drafts so we can
  // exclude their field definitions from the regular custom-field column path.
  const completedDraftConfigIds = useMemo(
    () => new Set(messageDrafts.map(d => d.id)),
    [messageDrafts]
  );

  // While a draft is in "preview" or "generating" state it is NOT yet in messageDrafts,
  // so its field definitions are still in contactFields.fieldDefinitions.  We synthesise
  // lightweight draft objects from those definitions so we can render them through the
  // same message-column path (with a visual "preview" indicator) instead of as plain
  // custom-field columns.
  const draftModeMessageGroups = useMemo(() => {
    const draftFields = contactFields.fieldDefinitions.filter(
      f => f.source === 'message_draft' && !completedDraftConfigIds.has(f.config_id)
    );

    const byConfig = {};
    for (const f of draftFields) {
      if (!byConfig[f.config_id]) byConfig[f.config_id] = [];
      byConfig[f.config_id].push(f);
    }

    return Object.values(byConfig).map(fields => {
      // field_name is the backend property key: "subject" or "body"
      const subjectField = fields.find(f => f.field_name === 'subject');
      const bodyField = fields.find(f => f.field_name === 'body');
      const baseField = subjectField ?? bodyField ?? fields[0];

      // display_names follow the pattern "<Type> Subject" / "<Type> Body"
      // e.g. "Email Subject", "Email Body", "InMail Body", "LinkedIn Message Body"
      // Strip the trailing " Subject" or " Body" word to get a clean group label.
      const emailName = (baseField?.display_name ?? '')
        .replace(/\s+(Subject|Body)$/, '')
        .trim() || 'Draft';

      const hasSubject = !!subjectField;
      // Any field having status "generating" means the approval fanout is running.
      const draftStatus = fields.some(f => f.status === 'generating') ? 'generating' : 'preview';

      return {
        id: baseField?.config_id,
        emailName,
        subjectFieldId: subjectField?.field_id ?? null,
        bodyFieldId: bodyField?.field_id ?? null,
        // typeOfMessage/messageType determine whether a Subject column is rendered.
        // Derive from presence of a Subject field: no subject → LinkedIn message type.
        typeOfMessage: hasSubject ? 'email' : 'linkedin',
        messageType: hasSubject ? 'new_thread' : 'message',
        subjectTemplate: '',
        bodyTemplate: '',
        isDraftPreview: true,
        draftStatus,
      };
    }).filter(g => g.id && g.bodyFieldId);
  }, [contactFields.fieldDefinitions, completedDraftConfigIds]);

  // Exclude ALL message_draft fields from the regular custom-field column path.
  // Approved drafts render via messageDrafts; preview/generating drafts via draftModeMessageGroups.
  const renderableContactFieldDefs = useMemo(
    () => contactFields.fieldDefinitions.filter(f => f.source !== 'message_draft'),
    [contactFields.fieldDefinitions]
  );

  // Tracking field IDs — resolved from field definitions with source === 'tracking'
  const contactedFieldId = useMemo(() => {
    const def = contactFields.fieldDefinitions.find(f => f.source === 'tracking' && f.field_name === 'contacted');
    return def?.field_id || null;
  }, [contactFields.fieldDefinitions]);

  const notesFieldId = useMemo(() => {
    const def = contactFields.fieldDefinitions.find(f => f.source === 'tracking' && f.field_name === 'notes');
    return def?.field_id || null;
  }, [contactFields.fieldDefinitions]);

  // Callbacks for updating tracking fields via the standard custom field value update path
  const handleContactedChange = useCallback(async (membershipId, fieldId, value) => {
    contactFields.updateFieldValue(membershipId, fieldId, value);
    // Also sync selectedContact's custom_fields optimistically
    setSelectedContact(prev => {
      if (!prev || prev.membership_id !== membershipId) return prev;
      return { ...prev, custom_fields: { ...(prev.custom_fields || {}), [fieldId]: value } };
    });
  }, [contactFields]);

  const handleNotesChange = useCallback(async (membershipId, fieldId, value) => {
    contactFields.updateFieldValue(membershipId, fieldId, value);
    setSelectedContact(prev => {
      if (!prev || prev.membership_id !== membershipId) return prev;
      return { ...prev, custom_fields: { ...(prev.custom_fields || {}), [fieldId]: value } };
    });
  }, [contactFields]);

  // Combined array used everywhere message columns are rendered.
  // Draft-preview groups appear first so the approved columns follow naturally.
  const allMessageDrafts = useMemo(
    () => [...draftModeMessageGroups, ...messageDrafts],
    [draftModeMessageGroups, messageDrafts]
  );

  // Phase 3.5 — advisor-side compliance overlay. Pure derivation from
  // the contacts response sideload + the already-enriched custom_fields
  // map. Falls back to an inert set of stubs when no reviewers are on
  // the campaign, so rendering code can stay branchless.
  // Feed the hook the *raw* contacts straight from the API response.
  // `enrichedContacts` goes through `useCustomFields.enrichEntityData`,
  // which rebuilds `custom_fields` from the advisor-visible field
  // definitions list only — and `source='compliance'` fields are
  // deliberately kept off that list, so the enrichment pipeline strips
  // every decision blob. `contactsData` still carries the original
  // server-attached `custom_fields` map the hook needs to look up
  // verdicts.
  //
  // `enrichedContacts` is passed alongside so the hook's client-side
  // staleness overlay can read the live (post-edit) subject / body
  // values — without this, `is_stale` would stay frozen at whatever the
  // server stamped during the last contacts GET, and the chip wouldn't
  // flip to amber until the next hard refresh.
  const advisorCompliance = useAdvisorCompliance({
    contacts: contactsData,
    enrichedContacts,
    messageDrafts: allMessageDrafts,
    complianceFieldMap,
    hasComplianceReviewers,
  });

  /**
   * Plan B Pattern A2 — Linear-style hard filter.
   *
   * After every edit that mutates a draft `(subject, body)`, classify
   * the row's post-edit state and surgically remove it from the table
   * if it no longer matches the active chip filter.
   *
   * Why inline-hash and not `advisorCompliance.contactMatchesFilter`:
   * the matcher reads from `currentContentHashes`, which is an async
   * `useEffect` that recomputes hashes one render after `contactsData`
   * settles. Awaiting that indirection from an edit handler means
   * either polling or wiring an effect — both fragile. Computing the
   * one new hash inline (`computeComplianceContentHash`) is a single
   * `crypto.subtle.digest` call (~0.1ms) and lets the handler decide
   * synchronously, with no chance of "row disappeared a beat after the
   * toast" UX. The other drafts on the row are unchanged by this edit
   * so we read their decision blobs straight off `contact.custom_fields`
   * with no further hashing.
   *
   * No-ops cleanly when:
   *  - The active chip filter is "All" (every row matches).
   *  - Compliance UI is disabled for this campaign.
   *  - The edit isn't to a draft subject/body (only those two fields
   *    flow into the hash; other contact-field edits never affect
   *    classification).
   *  - The contact doesn't carry a `membership_id` (defensive — should
   *    never happen from the table).
   */
  const surgicallyRemoveRowAfterEdit = useCallback(async ({
    contact,
    editedFieldId,
    newValue,
  }) => {
    const activeFilter = advisorCompliance.filter;
    if (!advisorCompliance.isEnabled) return;
    if (!activeFilter || activeFilter === COMPLIANCE_FILTERS.ALL) return;
    if (!contact?.membership_id) return;
    if (!editedFieldId) return;

    // Identify which (if any) draft owns the edited field. If none,
    // the edit didn't touch a subject/body so classification can't
    // change and we exit early.
    const editedDraft = allMessageDrafts.find(
      (d) => d?.subjectFieldId === editedFieldId || d?.bodyFieldId === editedFieldId,
    );
    if (!editedDraft) return;

    const isSubjectEdit = editedDraft.subjectFieldId === editedFieldId;
    const customFields = contact.custom_fields || {};

    // Build the post-edit (subject, body) pair for the edited draft.
    // The opposite half (body when editing subject, or vice-versa)
    // hasn't changed; reading from the raw server-attached
    // `custom_fields` is correct because it's the value the server
    // hash will see on the next reconcile.
    const newSubject = isSubjectEdit
      ? newValue
      : (customFields[editedDraft.subjectFieldId] ?? null);
    const newBody = isSubjectEdit
      ? (customFields[editedDraft.bodyFieldId] ?? null)
      : newValue;

    let editedDraftIsStale = true; // Default: assume stale if we can't hash.
    const editedComplianceFieldId = complianceFieldMap[String(editedDraft.id)];
    const editedDecision = editedComplianceFieldId
      ? customFields[editedComplianceFieldId]
      : null;
    const reviewedHash = editedDecision?.reviewed_content_hash;
    if (reviewedHash) {
      try {
        const currentHash = await computeComplianceContentHash(newSubject, newBody);
        editedDraftIsStale = currentHash !== reviewedHash;
      } catch {
        // Web Crypto unavailable — keep the conservative default
        // (treat as stale). Worst case we remove a row that the
        // server later confirms is still matching; the next
        // chip-click refetch will bring it back.
      }
    }

    // Walk every draft on this row through the same matcher the
    // server uses (`matchesComplianceFilter` is the single source of
    // truth shared between client and server). Bail out as soon as
    // any draft matches — that's enough to keep the row.
    //
    // Source for is_stale per draft:
    //   - The just-edited draft: our inline-computed hash above. The
    //     reactive overlay in `useAdvisorCompliance` hasn't run yet
    //     (it's an effect that settles a render or two later), so we
    //     must compute synchronously to give the correct verdict
    //     within this handler.
    //   - All other drafts: `advisorCompliance.getDecision`, which
    //     reads the *live* hash overlay computed from prior edits.
    //     Crucially, this is NOT the same as `customFields[fieldId]`
    //     directly: `customFields.is_stale` is the server's verdict
    //     from the last page load and does NOT reflect any edits the
    //     user has made in this session. Reading the raw blob would
    //     wrongly count a draft the user edited two minutes ago as
    //     still fresh, keeping the row visible until a filter
    //     round-trip.
    for (const draft of allMessageDrafts) {
      if (!draft?.id) continue;

      let effectiveDecision;
      if (draft.id === editedDraft.id) {
        const complianceFieldId = complianceFieldMap[String(draft.id)];
        const rawDecision = complianceFieldId ? customFields[complianceFieldId] : null;
        if (!rawDecision) {
          effectiveDecision = null;
        } else if (Boolean(rawDecision.is_stale) !== editedDraftIsStale) {
          effectiveDecision = { ...rawDecision, is_stale: editedDraftIsStale };
        } else {
          effectiveDecision = rawDecision;
        }
      } else {
        // Live overlaid value — knows about prior edits' hashes.
        effectiveDecision = advisorCompliance.getDecision(contact, draft.id);
      }

      if (matchesComplianceFilter(effectiveDecision, activeFilter)) {
        return; // Row still matches the active chip — keep it.
      }
    }

    // No draft on this row matches the active filter anymore.
    removeContactRow(contact.membership_id);
  }, [
    advisorCompliance.filter,
    advisorCompliance.isEnabled,
    advisorCompliance.getDecision,
    allMessageDrafts,
    complianceFieldMap,
    removeContactRow,
  ]);

  // Wire the helper into the early-declared `useCellEditing` callback
  // via the bridge ref. Keeping the assignment in an effect keeps the
  // ref stable across strict-mode double-invocation in dev.
  useEffect(() => {
    surgicallyRemoveRowAfterEditRef.current = surgicallyRemoveRowAfterEdit;
  }, [surgicallyRemoveRowAfterEdit]);

  // Fetch campaign-wide compliance counts from the backend so the summary
  // strip shows accurate totals for ALL contacts, not just those loaded.
  //
  // The backend now owns every bucket including `stale` (server-side
  // hash compare against current per-contact subject/body values), so
  // this is the only source of truth for chip numbers. The previous
  // client-side stale overlay (which required eager-loading every row
  // and subtracting per-status stale discoveries from headline buckets)
  // is gone; mutations invalidate this query so the strip refreshes on
  // save instead of relying on every contact already being in memory.
  //
  // Plan A note on totals: when a chip filter is active, the contacts
  // endpoint returns the *filter-scoped* `total_contacts` (correct for
  // the table footer). The chip endpoint needs the *unfiltered* total
  // to multiply by `num_drafts`, so we read it from
  // `getUnfilteredTotalContacts()` — cached across filter flips — and
  // fall back to `totalContacts` only on the very first fetch when
  // no unfiltered total has been observed yet (the user always lands
  // on "All" by default, so this fallback path is rare).
  const unfilteredTotalContacts = getUnfilteredTotalContacts() || totalContacts;
  const { data: backendComplianceSummary, isPending: isComplianceSummaryPending } = useQuery({
    queryKey: ['complianceSummary', campaignId, unfilteredTotalContacts],
    queryFn: async () => {
      const { data } = await api.get(
        `/api/campaigns/${campaignId}/compliance-summary`,
        { params: { total_contacts: unfilteredTotalContacts } },
      );
      return data;
    },
    enabled: Boolean(campaignId && hasComplianceReviewers && unfilteredTotalContacts > 0),
    staleTime: 30 * 1000,
  });

  // Direct pass-through of the backend payload, mapped to the camelCase
  // shape `ComplianceSummaryStrip` consumes. Falls back to the local
  // `useAdvisorCompliance.summary` only while the query is loading or
  // disabled (no reviewers, no contacts loaded yet) — same shape, just
  // computed from whatever rows are currently in memory.
  const mergedComplianceSummary = useMemo(() => {
    if (!backendComplianceSummary || backendComplianceSummary.total === 0) {
      return advisorCompliance.summary;
    }
    return {
      total: backendComplianceSummary.total,
      compliant: backendComplianceSummary.compliant,
      nonCompliant: backendComplianceSummary.non_compliant,
      needsHumanReview: backendComplianceSummary.needs_human_review || 0,
      notReviewed: backendComplianceSummary.not_reviewed,
      stale: backendComplianceSummary.stale || 0,
    };
  }, [backendComplianceSummary, advisorCompliance.summary]);

  const nonTrackingContactFieldCount = useMemo(
    () => renderableContactFieldDefs.filter(f => f.source !== 'tracking').length,
    [renderableContactFieldDefs]
  );

  // Address column is hidden until this campaign has unlocked at least one
  // address (any contact with address_fetched). Keeps unused campaigns clean.
  const showAddressColumn = useMemo(
    () => contactsData.some(c => c.address_fetched),
    [contactsData]
  );

  const CONTACT_COLUMNS = useMemo(() => {
    return getContactColumns(nonTrackingContactFieldCount, showAddressColumn);
  }, [nonTrackingContactFieldCount, showAddressColumn]);
  
  // A campaign becomes read-only for edits the moment any export record exists.
  const isLocked = exportTracking.length > 0;

  // Pre-compute Set<membership_id> per export doc — O(1) per-row lookup
  const processedExportTracking = useMemo(() => {
    return exportTracking.map(exp => ({
      ...exp,
      contactSet: new Set(exp.exported_contact_ids),
    }));
  }, [exportTracking]);

  // Set of message_group_ids that have at least one export, grouped by platform
  const { exportedGroupIdsSmartlead, exportedGroupIdsHeyreach, exportedGroupIdsGillyreach } = useMemo(() => {
    const smartlead = new Set();
    const heyreach = new Set();
    const gillyreach = new Set();
    for (const exp of processedExportTracking) {
      const platform = exp.platform || 'smartlead';
      const target = platform === 'heyreach' ? heyreach : platform === 'gillyreach' ? gillyreach : smartlead;
      for (const gid of (exp.message_group_ids || [])) {
        target.add(gid);
      }
    }
    return {
      exportedGroupIdsSmartlead: smartlead,
      exportedGroupIdsHeyreach: heyreach,
      exportedGroupIdsGillyreach: gillyreach,
    };
  }, [processedExportTracking]);

  const visibleSmartleadExportedCount = useMemo(() => {
    let count = 0;
    for (const gid of exportedGroupIdsSmartlead) {
      if (!collapsedExportGroupIds.has(gid)) count += 1;
    }
    return count;
  }, [exportedGroupIdsSmartlead, collapsedExportGroupIds]);

  const visibleHeyreachExportedCount = useMemo(() => {
    let count = 0;
    for (const gid of exportedGroupIdsHeyreach) {
      if (!collapsedExportGroupIds.has(gid)) count += 1;
    }
    return count;
  }, [exportedGroupIdsHeyreach, collapsedExportGroupIds]);

  const visibleGillyreachExportedCount = useMemo(() => {
    let count = 0;
    for (const gid of exportedGroupIdsGillyreach) {
      if (!collapsedExportGroupIds.has(gid)) count += 1;
    }
    return count;
  }, [exportedGroupIdsGillyreach, collapsedExportGroupIds]);

  // GillyReach per-(contact, draft) status — Map<"membership_id:message_draft_id", entry[]>
  // Keyed by the composite (membership, draft) so each step's column reads only
  // its own slice. Multiple entries per key only when the same contact appears
  // in two sequences that share a draft (rare, but the renderer stacks them).
  const [gillyreachContactStatus, setGillyreachContactStatus] = useState(() => new Map());
  const gillyreachHasPendingRef = useRef(true);

  /** Single source of truth for GET /api/sequencer/campaign-status (poller + imperative refresh). */
  const refreshGillyreachCampaignStatus = useCallback(async () => {
    if (!campaignId || exportedGroupIdsGillyreach.size === 0) return;
    try {
      const resp = await api.get('/api/sequencer/campaign-status', {
        params: { campaign_id: campaignId },
      });
      const statusMap = new Map();
      for (const c of (resp.data?.contacts || [])) {
        if (!c.membership_id || !c.message_draft_id) continue;
        const key = `${c.membership_id}:${c.message_draft_id}`;
        const existing = statusMap.get(key);
        if (existing) {
          existing.push(c);
        } else {
          statusMap.set(key, [c]);
        }
      }
      setGillyreachContactStatus(statusMap);
      gillyreachHasPendingRef.current = resp.data?.has_pending ?? false;
    } catch (_) {
      // Non-critical — table still renders, retry on next poll / visibility refresh
    }
  }, [campaignId, exportedGroupIdsGillyreach.size]);

  useEffect(() => {
    if (exportedGroupIdsGillyreach.size === 0 || !campaignId) {
      setGillyreachContactStatus(new Map());
      return;
    }

    let cancelled = false;
    let timeoutId = null;

    const pollTick = async () => {
      await refreshGillyreachCampaignStatus();
      if (cancelled) return;
      if (gillyreachHasPendingRef.current) {
        timeoutId = setTimeout(pollTick, 15_000);
      }
    };

    pollTick();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && gillyreachHasPendingRef.current && !cancelled) {
        refreshGillyreachCampaignStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [campaignId, exportedGroupIdsGillyreach.size, refreshGillyreachCampaignStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const CONTACT_TABLE_MIN_WIDTH = useMemo(() => {
    const baseWidth = getContactTableMinWidth(nonTrackingContactFieldCount, showAddressColumn);
    const messageWidth = getMessageColumnsWidth(
      allMessageDrafts,
      visibleSmartleadExportedCount,
      visibleHeyreachExportedCount,
      visibleGillyreachExportedCount
    );
    return baseWidth + messageWidth;
  }, [
    nonTrackingContactFieldCount,
    showAddressColumn,
    allMessageDrafts,
    visibleSmartleadExportedCount,
    visibleHeyreachExportedCount,
    visibleGillyreachExportedCount,
  ]);

  // Calculate height based on viewport
  useEffect(() => {
    const calculateHeight = () => {
      const windowHeight = window.innerHeight;
      const headerHeight = 120;
      const campaignResultsHeaderHeight = 60;
      const tabsHeight = 40;
      const padding = 20;
      
      const calculatedHeight = windowHeight - headerHeight - campaignResultsHeaderHeight - tabsHeight - padding;
      const finalHeight = Math.max(400, calculatedHeight);
      
      setContainerHeight(finalHeight);
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);

    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, []);
  
  // Measure container width once on mount (only used for initial loading check)
  useEffect(() => {
    if (!widthMeasureRef.current) return;
    
    // Single measurement - we only need to know if width > 0
    const width = widthMeasureRef.current.getBoundingClientRect().width;
    if (width > 0) {
      setContainerWidth(width);
    }
  }, [activeTab, companiesData.length, contactsData.length]);

  // Measure container height for List component
  useEffect(() => {
    const measureContainerHeight = () => {
      const currentRef = activeTab === 0 ? companiesOuterContainerRef : contactsOuterContainerRef;
      
      if (currentRef.current) {
        const outerHeight = currentRef.current.clientHeight;
        const headerHeight = 50;
        const availableHeight = outerHeight - headerHeight;
        if (availableHeight > 0) {
          setListHeight(availableHeight);
        }
      }
    };

    measureContainerHeight();
    const timeoutId1 = setTimeout(measureContainerHeight, 0);
    const timeoutId2 = setTimeout(measureContainerHeight, 50);
    const timeoutId3 = setTimeout(measureContainerHeight, 150);

    const currentRef = activeTab === 0 ? companiesOuterContainerRef : contactsOuterContainerRef;
    let resizeObserver;
    if (currentRef.current) {
      resizeObserver = new ResizeObserver(() => {
        measureContainerHeight();
      });
      resizeObserver.observe(currentRef.current);
    }

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [activeTab, companiesData.length, contactsData.length, containerHeight]);

  // Handle success parameter from Stripe redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      toast({
        title: 'Thank you for your purchase!',
        description: 'Your payment has been processed successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [toast]);

  // Clear selection and scroll to top when switching tabs
  useEffect(() => {
    clearSelection();
    
    // Scroll to top when switching tabs
    scrollToTopSafely();
  }, [activeTab, clearSelection, scrollToTopSafely]);

  // Reset list cache when data changes (but don't scroll - let infinite scroll maintain position)
  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
      // Don't scroll to top here - this would break infinite scroll
      // Scrolling happens explicitly when: switching tabs, rejecting previews, etc.
    }
  }, [companiesData, contactsData, activeTab]);

  // Track if this is the initial load (for auto-switching to non-empty tab)
  const isInitialLoadRef = useRef(true);
  
  // Reset initial load flag when campaign changes
  useEffect(() => {
    isInitialLoadRef.current = true;
    setSearchInput('');
    setAppliedSearch('');
  }, [campaignId]);

  // Apply search only on explicit submit (Enter key).
  const applySearch = useCallback(() => {
    if (!campaignId) return;
    const nextSearch = searchInput.trim();
    setAppliedSearch(nextSearch);
    fetchTabData(activeTab, 0, false, nextSearch);
    setTimeout(() => scrollToTopSafely(), 0);
  }, [campaignId, searchInput, fetchTabData, activeTab, scrollToTopSafely]);
  
  // Initial data load
  useEffect(() => {
    if (campaignId && shouldFetchInitialData) {
      fetchTabData(activeTab, 0, false);
    }
  }, [campaignId, shouldFetchInitialData, fetchTabData, activeTab]);
  
  // Auto-switch to non-empty tab (handles both initial load and dynamic data arrival)
  useEffect(() => {
    // Don't run if user has manually changed tabs (they want to view the current tab)
    if (!isInitialLoadRef.current) return;
    
    // Wait for data to be fetched (not loading and we have counts)
    if (isLoadingTabData) return;
    
    // Check if we have any data at all
    const hasAnyData = totalCompanies > 0 || totalContacts > 0;
    if (!hasAnyData) return;
    
    // If companies tab is active but empty, and contacts has data, switch to contacts
    // This handles both: initial page load AND when user adds contacts first in a new campaign
    if (activeTab === 0 && totalCompanies === 0 && totalContacts > 0) {
      console.log('[VirtualizedPersonaResults] Auto-switching to contacts tab (companies empty, contacts has data)');
      isInitialLoadRef.current = false;
      // Switch tab and fetch data
      setActiveTab(1);
      fetchTabData(1, 0, false);
      // Scroll to top
      setTimeout(() => scrollToTopSafely(), 50);
      return;
    }
    
    // If contacts tab is active but empty, and companies has data, switch to companies
    if (activeTab === 1 && totalContacts === 0 && totalCompanies > 0) {
      console.log('[VirtualizedPersonaResults] Auto-switching to companies tab (contacts empty, companies has data)');
      isInitialLoadRef.current = false;
      // Switch tab and fetch data
      setActiveTab(0);
      fetchTabData(0, 0, false);
      // Scroll to top
      setTimeout(() => scrollToTopSafely(), 50);
      return;
    }
    
    // If we reach here and have data, mark initial load as complete
    if (hasAnyData) {
      isInitialLoadRef.current = false;
    }
  }, [activeTab, totalCompanies, totalContacts, isLoadingTabData, fetchTabData, scrollToTopSafely]);

  useEffect(() => {
    const handler = async () => {
      contactFields.clearPreviewState();
      contactPreviewTypeRef.current = null;
      customFieldGenerationInProgressRef.current = false;
      setIsGeneratingCustomFields(false);
      if (contactFields.refetchDefinitions) {
        try {
          await contactFields.refetchDefinitions();
        } catch (_) {
          /* non-critical */
        }
      }
      await fetchTabData(activeTab, 0, false);
      if (campaignId) {
        queryClient.invalidateQueries({ queryKey: ['complianceSummary', campaignId] });
      }
    };
    window.addEventListener('mdpDraftDiscarded', handler);
    window.addEventListener('messageDraftPreviewRestored', handler);
    return () => {
      window.removeEventListener('mdpDraftDiscarded', handler);
      window.removeEventListener('messageDraftPreviewRestored', handler);
    };
  }, [contactFields, activeTab, fetchTabData, queryClient, campaignId]);

  useEffect(() => {
    const handler = () => fetchTabData(1, 0, false);
    window.addEventListener('contactsEnrichedAndVerified', handler);
    return () => window.removeEventListener('contactsEnrichedAndVerified', handler);
  }, [fetchTabData]);

  useEffect(() => {
    const handler = () => fetchTabData(activeTab, 0, false);
    window.addEventListener('messageDraftPreviewRegenerated', handler);
    return () => window.removeEventListener('messageDraftPreviewRegenerated', handler);
  }, [fetchTabData, activeTab]);

  // Track custom field generation state based on status events (completion_percentage: 100)
  // This is the most reliable method - the backend explicitly tells us when it's done
  useEffect(() => {
    if (onStreamingDataUpdate) {
      onStreamingDataUpdate((update) => {
        // Track custom field generation based on actual streaming events
        if (update.type === 'custom_field_definition') {
          // Definition received - generation is starting
          customFieldGenerationInProgressRef.current = true;
          setIsGeneratingCustomFields(true);
          
          // When the agent tool generates email drafts directly (bypassing the modal),
          // the ref is never set via handleGenerateEmailDrafts. Detect it here via the
          // source field so the approve banner routes to the email endpoint, not custom fields.
          if (update.data?.entity_type === 'contact' && update.data?.source === 'message_draft') {
            contactPreviewTypeRef.current = 'email_draft';
          }
          
          // Clear any existing completion timer
          if (customFieldCompletionTimerRef.current) {
            clearTimeout(customFieldCompletionTimerRef.current);
            customFieldCompletionTimerRef.current = null;
          }
        } else if (update.type === 'custom_field_update') {
          // Values are being generated - ensure flag stays true
          if (!customFieldGenerationInProgressRef.current) {
            customFieldGenerationInProgressRef.current = true;
            setIsGeneratingCustomFields(true);
          }
        } else if (update.type === 'status') {
          // Check if this is a custom field generation status update
          const completionPercentage = update.data?.completion_percentage;
          const op = update.data?.operation_id;
          const isCustomFieldOp =
            op === 'custom_field_generation' || op === 'contact_custom_field_generation';
          
          if (completionPercentage !== undefined) {
            // If generation is in progress (< 100%), ensure flag is set
            // This handles reconnection where cached status arrives before definition events
            if (completionPercentage < 100 && !customFieldGenerationInProgressRef.current) {
              customFieldGenerationInProgressRef.current = true;
              setIsGeneratingCustomFields(true);
              console.log('[VirtualizedPersonaResults] Status indicates active generation - setting flag');
            }

            // Restore the run-for-all button guard when the observer relays an
            // in-progress custom_field_generation status (e.g. after a page refresh).
            if (isCustomFieldOp) {
              if (completionPercentage < 100) {
                setIsResearchRunning(true);
              } else {
                setIsResearchRunning(false);
              }
            }
            
            // When we hit 100%, the backend is done - set flag immediately (no timer needed)
            if (completionPercentage >= 100 && customFieldGenerationInProgressRef.current) {
              // Clear any existing timer
              if (customFieldCompletionTimerRef.current) {
                clearTimeout(customFieldCompletionTimerRef.current);
                customFieldCompletionTimerRef.current = null;
              }
              
              // Set flag immediately - React will batch the state update with the next render
              customFieldGenerationInProgressRef.current = false;
              setIsGeneratingCustomFields(false);
              
              // Dispatch event to update header credit count
              window.dispatchEvent(new Event('customFieldCompleted'));
              
              // Trigger resize by incrementing completion counter
              console.log('[VirtualizedPersonaResults] Custom field generation completed - triggering row resize');
              setCustomFieldCompletionCounter(prev => prev + 1);
            }
          }
        }
        
        handleStreamingUpdate(update, activeTab);
      });
    }
  }, [handleStreamingUpdate, onStreamingDataUpdate, activeTab, companyFields.previewConfigId, contactFields.previewConfigId]);

  // Keep contactPreviewTypeRef in sync with the persisted source on field definitions.
  // This handles page-load / reconnect: if a contact preview config comes from
  // message_draft (email), the ref must be 'email_draft' so handleApprovePreview
  // routes to the email endpoint rather than the custom-fields endpoint.
  useEffect(() => {
    if (!contactFields.previewConfigId) {
      // Preview cleared — reset the type unless it was set by the modal flow
      // (modal flow sets it directly; we only reset from field-definition data)
      return;
    }
    const previewField = contactFields.fieldDefinitions.find(
      f => f.config_id === contactFields.previewConfigId && f.source === 'message_draft'
    );
    if (previewField) {
      contactPreviewTypeRef.current = 'email_draft';
    }
  }, [contactFields.previewConfigId, contactFields.fieldDefinitions]);

  // Expose refresh handler to parent
  useEffect(() => {
    if (onRefreshReady) {
      onRefreshReady({
        refresh: async () => {
          await fetchTabData(activeTab, 0, false);
        },
        initialFetch: async () => {
          await fetchTabData(activeTab, 0, false);
        },
        refreshGillyreachSequencerStatus: refreshGillyreachCampaignStatus,
      });
    }
  }, [fetchTabData, activeTab, onRefreshReady, refreshGillyreachCampaignStatus]);

  // Handle tab change
  const handleTabChange = useCallback((newTabIndex) => {
    // Mark initial load as complete when user manually changes tabs
    // This prevents auto-switching when user intentionally views an empty tab
    isInitialLoadRef.current = false;
    
    setActiveTab(newTabIndex);
    fetchTabData(newTabIndex, 0, false, appliedSearch);
    
    // Scroll to top when changing tabs
    scrollToTopSafely();
  }, [fetchTabData, appliedSearch, scrollToTopSafely]);
  
  // Listen for refetch event from insufficient credits modal
  useEffect(() => {
    const handleRefetchCustomFields = () => {
      console.log('VirtualizedPersonaResults: Received refetchCustomFields event - reloading page');
      // Reload the page to restore preview banner (simplest solution that works)
      window.location.reload();
    };
    
    window.addEventListener('refetchCustomFields', handleRefetchCustomFields);
    
    return () => {
      window.removeEventListener('refetchCustomFields', handleRefetchCustomFields);
    };
  }, []);

  // Listen for signup modal close event to reset generation flag
  useEffect(() => {
    const handleSignupModalClosed = () => {
      customFieldGenerationInProgressRef.current = false;
      setIsGeneratingCustomFields(false);
    };
    
    window.addEventListener('signupModalClosed', handleSignupModalClosed);
    
    return () => {
      window.removeEventListener('signupModalClosed', handleSignupModalClosed);
    };
  }, []);

  // Listen for credit warning cancel event to reset generation flag
  useEffect(() => {
    const handleCreditWarningCancelled = () => {
      customFieldGenerationInProgressRef.current = false;
      setIsGeneratingCustomFields(false);
    };
    
    window.addEventListener('creditWarningCancelled', handleCreditWarningCancelled);
    
    return () => {
      window.removeEventListener('creditWarningCancelled', handleCreditWarningCancelled);
    };
  }, []);

  // Handle custom field approval/rejection
  const handleApprovePreview = useCallback(async () => {
    const configId = activeTab === 0 ? companyFields.previewConfigId : contactFields.previewConfigId;
    if (!configId) return;

    // Determine preview type based on what triggered the contacts-tab preview
    const isEmailDraft = activeTab === 1 && contactPreviewTypeRef.current === 'email_draft';
    const operationType = isEmailDraft ? 'email_draft' : 'custom_field';

    const gate = chatCreditGateSession;
    if (chatCreditGateMatchesSession(gate, configId, operationType)) {
      try {
        await onSubmitChatCreditGate?.('accept');
      } catch (err) {
        console.error('[chat credit gate accept]', err);
      }
      return;
    }

    // Mark generation as starting (optimistic)
    customFieldGenerationInProgressRef.current = true;
    setIsGeneratingCustomFields(true);
    
    try {
      // Wrap approval in credit warning
      await withCreditWarning(
        operationType,
        {
          campaign_id: campaignId,
          version_id: activeVersionId,
          config_id: configId,
          entity_type: activeTab === 1 ? 'contact' : 'company',
        },
        async () => {
          // Don't clear preview state here - let it be cleared on successful completion
          // If there's a 402 error, preview state will remain for retry
          
          // Clear any existing completion timer
          if (customFieldCompletionTimerRef.current) {
            clearTimeout(customFieldCompletionTimerRef.current);
            customFieldCompletionTimerRef.current = null;
          }
          
          // Call the appropriate approval function based on preview type
          if (isEmailDraft) {
            kickstartEmailDraftFanoutUi(configId);
            emailOrchestration.approveEmailDrafts(configId);
            // Note: emailDraftCompleted event will be dispatched when streaming completes
          } else {
            // Approve custom fields
            if (onCustomFieldApprove) {
              onCustomFieldApprove(configId);
            }
            // Note: customFieldCompleted event will be dispatched when streaming completes (completion_percentage >= 100)
          }
        }
      );
    } catch (error) {
      customFieldGenerationInProgressRef.current = false;
      setIsGeneratingCustomFields(false);
    }
  }, [activeTab, companyFields, contactFields, onCustomFieldApprove, emailOrchestration, campaignId, activeVersionId, withCreditWarning, kickstartEmailDraftFanoutUi, chatCreditGateSession, onSubmitChatCreditGate]);

  const handleRejectPreview = useCallback(async () => {
    const configId = activeTab === 0 ? companyFields.previewConfigId : contactFields.previewConfigId;
    if (!configId) return;

    try {
      // Determine preview type based on what triggered the contacts-tab preview
      const isEmailDraft = activeTab === 1 && contactPreviewTypeRef.current === 'email_draft';
      const gate = chatCreditGateSession;
      if (chatCreditGateMatchesSession(gate, configId, isEmailDraft ? 'email_draft' : 'custom_field')) {
        await onSubmitChatCreditGate?.('reject');
        return;
      }
      
      // Call the appropriate API endpoint based on preview type
      if (isEmailDraft) {
        // Reject email drafts — hard-delete config, field definitions and values
        await api.delete(`/api/emails/${campaignId}/draft/configs/${configId}`);
      } else {
        // Reject custom fields — pass entity_type as a raw JSON string (FastAPI Body() str param)
        const entityType = activeTab === 0 ? 'company' : 'contact';
        await api.delete(`/api/custom-fields/campaigns/${campaignId}/configs/${configId}/reset`, {
          data: entityType
        });
      }
      
      // Clear preview state
      if (activeTab === 0) {
        companyFields.clearPreviewState();
      } else {
        contactFields.clearPreviewState();
        contactPreviewTypeRef.current = null;
      }
      
      // Clear generation flag since preview is rejected
      customFieldGenerationInProgressRef.current = false;
      setIsGeneratingCustomFields(false);
      
      // Clear any pending timer
      if (customFieldCompletionTimerRef.current) {
        clearTimeout(customFieldCompletionTimerRef.current);
        customFieldCompletionTimerRef.current = null;
      }
      
      await fetchTabData(activeTab, 0, false);
    } catch (error) {
      console.error('Error rejecting preview:', error);
      const detail = error.response?.data?.detail;
      const errorMessage = Array.isArray(detail)
        ? detail.map(d => d.msg).join(', ')
        : detail || 'Failed to reject preview';
      toast({
        title: 'Rejection Failed',
        description: errorMessage,
        status: 'error',
        duration: 3000,
      });
    }
  }, [campaignId, activeTab, companyFields, contactFields, fetchTabData, toast, chatCreditGateSession, onSubmitChatCreditGate]);

  // Handle custom field generation trigger
  const handleGenerateCustomFields = useCallback((prompt) => {
    if (!prompt || !campaignId) return;
    
    // Mark generation as starting
    customFieldGenerationInProgressRef.current = true;
    setIsGeneratingCustomFields(true);
    
    // Clear any existing completion timer
    if (customFieldCompletionTimerRef.current) {
      clearTimeout(customFieldCompletionTimerRef.current);
      customFieldCompletionTimerRef.current = null;
    }
    
    const entityType = activeTab === 0 ? 'companies' : 'contacts';
    if (activeTab === 1) contactPreviewTypeRef.current = 'custom_field';
    onCustomFieldGenerate?.(prompt, null, entityType);
  }, [campaignId, activeTab, onCustomFieldGenerate]);

  // Handle continue generation for incomplete custom fields
  const handleContinueGeneration = useCallback(async (fieldDef) => {
    if (!fieldDef || !fieldDef.config_id || !campaignId) return;
    
    const configId = fieldDef.config_id;
    const displayName = fieldDef.display_name;
    
    // Determine if this is an email field (Subject or Body on contacts tab)
    const isEmailField = activeTab === 1 && (displayName === 'Subject' || displayName === 'Body');
    const operationType = isEmailField ? 'email_draft' : 'custom_field';

    const gate = chatCreditGateSession;
    if (chatCreditGateMatchesSession(gate, configId, operationType)) {
      try {
        await onSubmitChatCreditGate?.('accept');
      } catch (err) {
        console.error('[chat credit gate accept]', err);
      }
      return;
    }
    
    // Mark generation as starting
    customFieldGenerationInProgressRef.current = true;
    setIsGeneratingCustomFields(true);
    
    // Clear any existing completion timer
    if (customFieldCompletionTimerRef.current) {
      clearTimeout(customFieldCompletionTimerRef.current);
      customFieldCompletionTimerRef.current = null;
    }
    
    try {
      await withCreditWarning(
        operationType,
        {
          campaign_id: campaignId,
          version_id: activeVersionId,
          config_id: configId,
          entity_type: activeTab === 1 ? 'contact' : 'company',
        },
        async () => {
          try {
            if (isEmailField) {
              kickstartEmailDraftFanoutUi(configId);
              emailOrchestration.approveEmailDrafts(configId);
            } else if (onCustomFieldApprove) {
              onCustomFieldApprove(configId);
            }
          } catch (error) {
            console.error('Error continuing generation:', error);
            customFieldGenerationInProgressRef.current = false;
            setIsGeneratingCustomFields(false);

            toast({
              title: 'Generation Failed',
              description: error.response?.data?.detail || 'Failed to continue generation',
              status: 'error',
              duration: 3000,
            });
          }
        }
      );
    } catch (error) {
      customFieldGenerationInProgressRef.current = false;
      setIsGeneratingCustomFields(false);
      if (!error?.isCancellation) {
        console.error('Continue generation cancelled or failed:', error);
        toast({
          title: 'Could not continue',
          description: error?.message || 'Something went wrong',
          status: 'error',
          duration: 3000,
        });
      }
    }
  }, [campaignId, activeVersionId, activeTab, onCustomFieldApprove, emailOrchestration, withCreditWarning, toast, kickstartEmailDraftFanoutUi, chatCreditGateSession, onSubmitChatCreditGate]);

  // Handle email verification
  const handleVerifyEmails = useCallback(async () => {
    if (!campaignId || !activeVersionId) return;
    
    try {
      // Pass selected contact IDs if any are selected, otherwise verify all
      const contactIds = selectedRows.size > 0 ? Array.from(selectedRows) : null;
      
      const result = await emailOrchestration.verifyEmails(contactIds);
      
      // Check if verification was blocked (403 signup required or returned early)
      // verifyEmails returns undefined when it encounters a 403 and returns early
      if (result === undefined) {
        // Signup modal or error modal already shown, don't show success toast
        return;
      }
      
      // Only show success toast if verification actually completed
      toast({
        title: 'Email Verification Complete',
        description: contactIds 
          ? `Verified ${contactIds.length} selected contact(s)` 
          : 'All contact emails have been verified',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      if (!error?.isCancellation) {
        console.error('Email verification failed:', error);
      }
    }
  }, [campaignId, activeVersionId, selectedRows, emailOrchestration, toast]);

  // Handle email draft generation
  const handleGenerateEmailDrafts = useCallback((draftingParams) => {
    if (!draftingParams || !campaignId || !activeVersionId) return;
    
    // Mark generation as starting
    customFieldGenerationInProgressRef.current = true;
    setIsGeneratingCustomFields(true);
    contactPreviewTypeRef.current = 'email_draft';
    
    // Clear any existing completion timer
    if (customFieldCompletionTimerRef.current) {
      clearTimeout(customFieldCompletionTimerRef.current);
      customFieldCompletionTimerRef.current = null;
    }
    
    // Trigger email draft preview (same config_id pattern as custom fields)
    const configId = contactFields.previewConfigId;
    void emailOrchestration.previewEmailDrafts(draftingParams, configId).catch((e) => {
      if (e?.isCancellation) {
        customFieldGenerationInProgressRef.current = false;
        setIsGeneratingCustomFields(false);
      }
    });
  }, [campaignId, activeVersionId, emailOrchestration, contactFields.previewConfigId]);

  // Handle delete selected rows
  const handleDeleteSelectedRows = useCallback(async () => {
    const entityType = activeTab === 0 ? 'companies' : 'contacts';
    const success = await handleDeleteSelected(selectedRows, entityType);
    if (success) {
      clearSelection();
    }
  }, [selectedRows, activeTab, handleDeleteSelected, clearSelection]);

  const handleSelectAllRows = useCallback(async (checked) => {
    if (!checked) {
      handleSelectAll(false);
      setIsSelectingAllRows(false);
      return;
    }

    setIsSelectingAllRows(true);
    const tabType = activeTab === 0 ? 'companies' : 'contacts';
    const loadedCount = activeTab === 0 ? companiesData.length : contactsData.length;
    const totalCount = activeTab === 0 ? totalCompanies : totalContacts;

    if (loadedCount >= totalCount) {
      handleSelectAll(true);
      setIsSelectingAllRows(false);
      return;
    }

    try {
      const response = await campaignService.getCampaign(
        campaignId,
        null,
        null,
        tabType,
        appliedSearch
      );

      const items = tabType === 'companies'
        ? (response.companies || [])
        : (response.contacts || []);

      const allIds = [];
      for (const item of items) {
        const itemId = tabType === 'companies'
          ? (item.elasticsearch_id || item.company_uuid || item.id)
          : (item.elasticsearch_id || item.id);
        if (itemId) allIds.push(itemId);
      }

      setSelectedRowsFromIds(allIds);
    } catch (error) {
      console.error('Failed to select all rows:', error);
      toast({
        title: 'Selection failed',
        description: 'Could not select all rows in this table.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSelectingAllRows(false);
    }
  }, [
    handleSelectAll,
    activeTab,
    companiesData.length,
    contactsData.length,
    totalCompanies,
    totalContacts,
    campaignId,
    appliedSearch,
    setSelectedRowsFromIds,
    toast,
  ]);

  // Handle contact enrichment
  const handleEnrichContacts = useCallback(async () => {
    if (activeTab !== 1) return; // Only for contacts tab
    
    try {
      if (selectedRows.size > 0) {
        // Enrich selected contacts
        const selectedContactIds = Array.from(selectedRows);
        await contactEnrichment.enrichContacts(selectedContactIds);
      } else {
        // Enrich all contacts (pass null to enrich all)
        await contactEnrichment.enrichContacts(null);
      }
    } catch (error) {
      // Error already handled in the hook
      console.error('Contact enrichment failed:', error);
    }
  }, [activeTab, selectedRows, contactEnrichment]);

  // Handle on-demand address fetch (Versium). Scopes to selected rows when any
  // are selected; otherwise fetches for all eligible contacts. The credit-warning
  // modal surfaces the per-contact cost and eligible count before charging.
  const handleFetchAddresses = useCallback(async () => {
    if (activeTab !== 1) return; // Only for contacts tab
    try {
      const ids = selectedRows.size > 0 ? Array.from(selectedRows) : null;
      await contactAddress.fetchAddresses(ids);
    } catch (error) {
      console.error('Address fetch failed:', error);
    }
  }, [activeTab, selectedRows, contactAddress]);

  // Create flattened data structure for virtual list
  const flattenedData = useMemo(() => {
    const result = [];
    const rawData = activeTab === 0 ? enrichedCompanies : enrichedContacts;
    const rowType = activeTab === 0 ? ROW_TYPES.COMPANY : ROW_TYPES.CONTACT;

    // Phase 3.5 — chip filter is server-side now. The contacts endpoint
    // returns only matching rows when `compliance_filter` is sent, so
    // there's nothing to filter client-side here. The previous in-memory
    // `rawData.filter(contactMatchesFilter)` step required eager-loading
    // the entire campaign so the predicate could see every row; both
    // are gone with Plan A.
    const currentData = rawData;

    currentData.forEach((item, index) => {
      result.push({
        id: item.id || `${rowType}_${index}`,
        type: rowType,
        data: item
      });
    });

    const isLoading = isLoadingTabData;
    const hasMore = hasMoreData;

    if (isLoading && hasMore) {
      result.push({
        id: 'loading_more',
        type: ROW_TYPES.EMPTY_STATE,
        data: {
          message: `Loading more ${activeTab === 0 ? 'companies' : 'contacts'}...`,
          isLoading: true
        }
      });
    }

    if (currentData.length === 0 && !isProcessing && !isLoading) {
      const entityLabel = activeTab === 0 ? 'companies' : 'contacts';
      const normalizedSearch = appliedSearch.trim();
      // Compliance filter message beats the search message when both are
      // active — it's the more recent user intent and clarifies why the
      // table is empty. Post-Plan A the filter is server-side, so a
      // zero-row response under an active chip means "matched zero" —
      // the rawData length check is no longer needed (and would be
      // wrong, since a filtered fetch returning zero matches would have
      // rawData.length === 0).
      const hasComplianceFilter = activeTab === 1
        && advisorCompliance.isEnabled
        && advisorCompliance.filter !== 'all';
      const message = hasComplianceFilter
        ? `No ${entityLabel} match the selected compliance filter`
        : (normalizedSearch
            ? `No ${entityLabel} found for "${normalizedSearch}"`
            : `No ${entityLabel} found`);
      result.push({
        id: 'empty_state',
        type: ROW_TYPES.EMPTY_STATE,
        data: {
          message,
          isLoading: false
        }
      });
    }

    return result;
  }, [
    activeTab,
    enrichedCompanies,
    enrichedContacts,
    isLoadingTabData,
    hasMoreData,
    isProcessing,
    appliedSearch,
    advisorCompliance.isEnabled,
    advisorCompliance.filter,
  ]);

  // Scroll handler for infinite loading with variable heights
  const lastScrollTriggerRef = useRef(0);
  const handleScroll = useCallback(({ scrollOffset, scrollUpdateWasRequested, scrollDirection }) => {
    const scrollHasMore = hasMoreData;
    const scrollIsLoading = isLoadingTabData;

    if (!scrollUpdateWasRequested && scrollHasMore && !scrollIsLoading && flattenedData.length > 0 && scrollDirection === 'forward') {
      const visibleHeight = listHeight;
      
      const estimatedAvgHeight = 80;
      const estimatedLastVisibleIndex = Math.floor((scrollOffset + visibleHeight) / estimatedAvgHeight);
      
      const triggerThreshold = Math.max(flattenedData.length - 10, flattenedData.length * 0.8);
      
      if (estimatedLastVisibleIndex >= triggerThreshold) {
        const now = Date.now();
        if (now - lastScrollTriggerRef.current > 1000) {
          lastScrollTriggerRef.current = now;
          loadMoreData(activeTab);
        }
      }
    }
  }, [hasMoreData, isLoadingTabData, loadMoreData, flattenedData.length, listHeight, activeTab]);

  // Phase 3.5 + Plan A — chip click triggers a server-side refetch
  // scoped to the new filter, instead of a client-side `array.filter`
  // over an eager-loaded full dataset. `fetchTabData` already detects
  // the filter change (lines ~63–75 in `useTableData.js`), resets
  // offsets, and bumps its in-flight version guard to discard any
  // previous-filter pages still in flight. We only update local
  // `advisorCompliance.filter` state for the active-chip UI styling.
  const handleAdvisorComplianceFilterChange = useCallback((nextFilter) => {
    advisorCompliance.setFilter(nextFilter);
    fetchTabData(1, 0, false, currentSearchRef.current, nextFilter);
  }, [advisorCompliance, fetchTabData, currentSearchRef]);

  // Handle cell edit for virtual table row
  const handleCellEditForRow = useCallback(async (entityId, fieldId, newValue, entityType) => {
    const targetData = entityType === 'companies' ? companiesData : contactsData;
    const fieldHook = entityType === 'companies' ? companyFields : contactFields;
    
    const entity = targetData.find(e => e.id === entityId);
    if (!entity || !entity.membership_id) return;
    
    const fieldDef = fieldHook.fieldDefinitions.find(f => f.field_id === fieldId);
    if (!fieldDef) return;
    
    try {
      await fieldHook.updateFieldValue(entity.membership_id, fieldId, newValue);

      // Plan A — same reasoning as the inline cell-editing handler
      // above: invalidate the chip summary on any contact edit so
      // stale-flips show up immediately.
      if (entityType === 'contacts' && campaignId) {
        queryClient.invalidateQueries({ queryKey: ['complianceSummary', campaignId] });
        // Plan B Pattern A2 — vanish row from the active chip view
        // immediately if it no longer matches.
        await surgicallyRemoveRowAfterEdit({
          contact: entity,
          editedFieldId: fieldId,
          newValue,
        });
      }

      toast({
        title: 'Field Updated',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      // Extract error message - handle both string and array formats
      let errorMessage = 'Failed to update field';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors - extract messages
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast({
        title: 'Update Failed',
        description: errorMessage,
        status: 'error',
        duration: 3000,
      });
    }
  }, [companiesData, contactsData, companyFields, contactFields, toast, campaignId, queryClient, surgicallyRemoveRowAfterEdit]);

  // Delete a custom field column and remove it from local state
  const handleDeleteCompanyField = useCallback(async (fieldDef) => {
    try {
      await campaignService.deleteCustomField(campaignId, fieldDef.field_id);
      companyFields.removeFieldDefinition(fieldDef.field_id);
      toast({ title: 'Column deleted', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      console.error('[VirtualizedPersonaResults] deleteCompanyField error:', err);
      toast({
        title: 'Failed to delete column',
        description: err.response?.data?.detail || err.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [campaignId, companyFields, toast]);

  const handleMessageDraftContinueGeneration = useCallback(async (draft) => {
    setGeneratingDraftId(draft.id);
    try {
      await withCreditWarning(
        'email_draft',
        {
          campaign_id: campaignId,
          version_id: activeVersionId,
          config_id: draft.id,
          entity_type: 'contact',
        },
        async () => {
          kickstartEmailDraftFanoutUi(draft.id);
          await emailOrchestration.approveEmailDrafts(draft.id);
          // Plan A — generation rewrites per-contact subject/body for
          // every (contact, draft) pair this run touched, which flips
          // any previously-reviewed cell stale. Refresh chip totals.
          if (campaignId) {
            queryClient.invalidateQueries({ queryKey: ['complianceSummary', campaignId] });
          }
          toast({
            title: 'Done',
            description: 'Messages generated for new contacts.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }
      );
    } catch (err) {
      if (!err?.isCancellation) {
        toast({
          title: 'Generation failed',
          description: err?.message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } finally {
      setGeneratingDraftId(null);
    }
  }, [emailOrchestration, withCreditWarning, campaignId, activeVersionId, toast, kickstartEmailDraftFanoutUi, queryClient]);

  const handleMessageDraftDeleteWithFieldRefresh = useCallback(async (draftId) => {
    // Snapshot which field definitions belong to this draft's config BEFORE the
    // optimistic removal drops the draft from messageDrafts / completedDraftConfigIds.
    const draftFieldIds = contactFields.fieldDefinitions
      .filter(f => f.config_id === draftId && f.source === 'message_draft')
      .map(f => f.field_id);

    await onMessageDraftDelete?.(draftId);

    // Optimistically remove the associated field definitions so they don't
    // re-appear as ghost columns (completedDraftConfigIds no longer hides them
    // once the draft is gone from messageDrafts).  This avoids a full
    // refetchDefinitions round-trip which can blank the table.
    draftFieldIds.forEach(id => contactFields.removeFieldDefinition(id));

    // Plan A — deleting a draft removes its compliance field definition,
    // which changes `num_drafts` and therefore every chip total
    // (`total = total_contacts × num_drafts`). Refresh the summary.
    if (campaignId) {
      queryClient.invalidateQueries({ queryKey: ['complianceSummary', campaignId] });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onMessageDraftDelete, contactFields.removeFieldDefinition, campaignId, queryClient]);

  const handleDeleteContactField = useCallback(async (fieldDef) => {
    try {
      await campaignService.deleteCustomField(campaignId, fieldDef.field_id);
      contactFields.removeFieldDefinition(fieldDef.field_id);
      toast({ title: 'Column deleted', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      console.error('[VirtualizedPersonaResults] deleteContactField error:', err);
      toast({
        title: 'Failed to delete column',
        description: err.response?.data?.detail || err.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [campaignId, contactFields, toast]);

  // Dynamic itemSize function for variable row heights
  // Create a NEW function reference when field values change to force react-window to recalculate
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getItemSize = useCallback((index) => {
    if (!flattenedData || index >= flattenedData.length) {
      return 80; // Default height
    }
    
    const rowData = flattenedData[index];
    const fieldDefinitions = activeTab === 0 ? companyFields.fieldDefinitions : contactFields.fieldDefinitions;
    
    return calculateRowHeight(rowData, fieldDefinitions);
  }, [
    flattenedData, 
    activeTab, 
    companyFields.fieldDefinitions, 
    contactFields.fieldDefinitions,
  ]);

  // Trigger resize ONLY when custom field generation completes
  // This ensures smooth streaming with a single resize at the end
  useEffect(() => {
    if (customFieldCompletionCounter > 0 && listRef.current && flattenedData.length > 0) {
      console.log('[VirtualizedPersonaResults] Completion detected - resizing all rows now:', {
        companyFieldCount: companyFields.fieldDefinitions.length,
        contactFieldCount: contactFields.fieldDefinitions.length,
        companyFieldValuesVersion: companyFields.fieldValuesVersion,
        contactFieldValuesVersion: contactFields.fieldValuesVersion,
        rowCount: flattenedData.length
      });
      
      // Give React time to commit all final updates, then trigger resize
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.resetAfterIndex(0, true);
          console.log('[VirtualizedPersonaResults] All rows resized to final heights');
        }
      }, 100);
    }
  }, [customFieldCompletionCounter, flattenedData.length, companyFields.fieldDefinitions.length, contactFields.fieldDefinitions.length, companyFields.fieldValuesVersion, contactFields.fieldValuesVersion]);

  // Memoize list data
  const listData = useMemo(() => ({
    flattenedData,
    selectedRows,
    onRowSelect: handleRowSelect,
    companyFieldDefinitions: companyFields.fieldDefinitions,
    contactFieldDefinitions: renderableContactFieldDefs,
    previewMembershipIds: activeTab === 0 ? companyFields.previewMembershipIds : contactFields.previewMembershipIds,
    editingCell,
    editingCellValue,
    setEditingCell,
    setEditingCellValue,
    handleCellEdit: handleCellEditForRow,
    onMessageDraftCellSave: async (membershipId, fieldId, newValue) => {
      // updateFieldValue handles both the optimistic in-memory update (so the cell
      // re-renders immediately) and the backend PUT call in one step.
      await contactFields.updateFieldValue(membershipId, fieldId, newValue);
      // Plan A — this is the only edit path that mutates a draft's
      // (subject, body) content directly, which is exactly the input the
      // server-side stale hash compares against. Without this invalidate
      // the chip strip stays at e.g. flagged=1996 / stale=0 even though
      // the row visually flipped to amber. Refetch is ~30ms.
      if (campaignId) {
        queryClient.invalidateQueries({ queryKey: ['complianceSummary', campaignId] });
        // Plan B Pattern A2 — Linear-style hard filter. If the edit
        // makes the row stop matching the active chip, splice it out
        // of the table immediately. The contact reference is looked
        // up by membership_id since this handler doesn't receive the
        // row object.
        const contact = contactsData.find(
          (c) => String(c.membership_id) === String(membershipId),
        );
        if (contact) {
          await surgicallyRemoveRowAfterEdit({
            contact,
            editedFieldId: fieldId,
            newValue,
          });
        }
      }
    },
    companyColumns: COMPANY_COLUMNS,
    contactColumns: CONTACT_COLUMNS,
    companyTableMinWidth: COMPANY_TABLE_MIN_WIDTH,
    contactTableMinWidth: CONTACT_TABLE_MIN_WIDTH,
    messageDrafts: allMessageDrafts,
    exportTracking: processedExportTracking,
    exportedGroupIdsSmartlead,
    exportedGroupIdsHeyreach,
    exportedGroupIdsGillyreach,
    gillyreachContactStatus,
    collapsedExportGroupIds,
    onCompanyClick: setSelectedCompany,
    onContactClick: setSelectedContact,
    contactedFieldId,
    notesFieldId,
    onContactedChange: handleContactedChange,
    onNotesChange: handleNotesChange,
    isGeneratingCustomFields,
    // Phase 3.5 advisor compliance — passed down to TableRow for the
    // inline chip next to the subject. `getDecision` is a stable
    // reference from useAdvisorCompliance; the chip click handler opens
    // the shared non-compliant diff modal.
    advisorComplianceEnabled: advisorCompliance.isEnabled,
    getAdvisorComplianceDecision: advisorCompliance.getDecision,
    onAdvisorComplianceChipClick: advisorCompliance.openNonCompliantModal,
    isLocked,
  }), [
    flattenedData, 
    selectedRows, 
    handleRowSelect, 
    companyFields.fieldDefinitions,
    renderableContactFieldDefs,
    companyFields.previewMembershipIds,
    contactFields.previewMembershipIds,
    activeTab,
    editingCell, 
    editingCellValue,
    setEditingCell,
    setEditingCellValue,
    handleCellEditForRow,
    contactFields.updateFieldValue,
    campaignId,
    contactsData,
    surgicallyRemoveRowAfterEdit,
    COMPANY_COLUMNS,
    CONTACT_COLUMNS,
    COMPANY_TABLE_MIN_WIDTH,
    CONTACT_TABLE_MIN_WIDTH,
    companyFields.fieldValuesVersion,
    contactFields.fieldValuesVersion,
    allMessageDrafts,
    processedExportTracking,
    exportedGroupIdsSmartlead,
    exportedGroupIdsHeyreach,
    exportedGroupIdsGillyreach,
    gillyreachContactStatus,
    collapsedExportGroupIds,
    contactedFieldId,
    notesFieldId,
    handleContactedChange,
    handleNotesChange,
    isGeneratingCustomFields,
    contactFields,
    advisorCompliance.isEnabled,
    advisorCompliance.getDecision,
    advisorCompliance.openNonCompliantModal,
    isLocked,
  ]);

  if (!campaignId) {
    return null;
  }

  const hasData = totalCompanies > 0 || totalContacts > 0;
  const hasActiveSearch = appliedSearch.trim().length > 0;
  
  if (!hasData && !hasActiveSearch) {
    // Show the friendly "Let's build your campaign" message
    return (
      <Box 
        h="100%" 
        display="flex" 
        alignItems="center" 
        justifyContent="center"
        textAlign="center"
        color="gray.400"
      >
        <VStack spacing={4}>
          <Text fontSize="lg" fontWeight="medium">
            Building your campaign...
          </Text>
        </VStack>
      </Box>
    );
  }

  // We have data - render the table
  return (
    <Box h="100%" w="100%" bg="#0a0a0a" borderRadius="md" display="flex" flexDirection="column">
      {/* Compact toolbar: Back to Home | Actions */}
      <Box pl={3} pr={2} py={1} borderBottom="1px solid" borderColor="rgba(255,255,255,0.08)" flexShrink={0}>
        <HStack justify="space-between" align="center" spacing={1}>
          {!isAnonymous ? (
            <Button
              leftIcon={<ChevronLeft size={20} strokeWidth={1.5} />}
              size="xs"
              variant="ghost"
              color="gray.400"
              fontSize="xs"
              height="28px"
              minW="auto"
              px={2}
              _hover={{
                color: "rgba(255,255,255,0.7)",
                bg: "transparent",
                boxShadow: "none",
              }}
              _active={{ bg: "transparent", boxShadow: "none" }}
              onClick={() => {
                if (selectedCompany) { setSelectedCompany(null); return; }
                if (selectedContact) { setSelectedContact(null); return; }
                navigate('/');
              }}
            >
            </Button>
          ) : (
            <Box />
          )}
          <Spacer />
          {!selectedCompany && !selectedContact && (
          <HStack spacing={1.5} minW={0}>
              {/* Action buttons - left of search bar */}
              {activeTab === 0 ? (
                <>
                  <Tooltip label="Add Custom Fields" placement="bottom" openDelay={400}>
                    <IconButton
                      aria-label="Add Custom Fields"
                      icon={<AddIcon />}
                      size="xs"
                      variant="ghost"
                      height="28px"
                      color="rgba(255,255,255,0.4)"
                      flexShrink={0}
                      isDisabled={!campaignId || totalCompanies === 0 || isStatusBarVisible}
                      _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                      _focus={{ boxShadow: "none" }}
                      onClick={onCustomFieldModalOpen}
                    />
                  </Tooltip>
                  <Tooltip label={isExporting ? "Downloading..." : "Download"} placement="bottom" openDelay={400}>
                    <IconButton
                      aria-label="Download"
                      icon={isExporting ? <Spinner size="xs" /> : <DownloadIcon />}
                      size="xs"
                      variant="ghost"
                      height="28px"
                      color="rgba(255,255,255,0.4)"
                      flexShrink={0}
                      isDisabled={!campaignId || isExporting}
                      _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                      _focus={{ boxShadow: "none" }}
                      onClick={() => handleExport(onNoCreditsModalOpen)}
                    />
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip label="Add Custom Fields" placement="bottom" openDelay={400}>
                    <IconButton
                      aria-label="Add Custom Fields"
                      icon={<AddIcon />}
                      size="xs"
                      variant="ghost"
                      height="28px"
                      color="rgba(255,255,255,0.4)"
                      flexShrink={0}
                      isDisabled={!campaignId || totalContacts === 0 || isStatusBarVisible}
                      _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                      _focus={{ boxShadow: "none" }}
                      onClick={onCustomFieldModalOpen}
                    />
                  </Tooltip>
                  <Tooltip
                    label={contactEnrichment.isEnriching ? "Enriching..." : selectedRows.size > 0 ? `Get Contact Info (${selectedRows.size})` : "Get Contact Info"}
                    placement="bottom"
                    openDelay={400}
                  >
                    <IconButton
                      aria-label="Get Contact Info"
                      icon={contactEnrichment.isEnriching ? <Spinner size="xs" /> : <UnlockIcon />}
                      size="xs"
                      variant="ghost"
                      height="28px"
                      color="rgba(255,255,255,0.4)"
                      flexShrink={0}
                      isDisabled={!campaignId || totalContacts === 0 || contactEnrichment.isEnriching}
                      _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                      _focus={{ boxShadow: "none" }}
                      onClick={handleEnrichContacts}
                    />
                  </Tooltip>
                  <Tooltip
                    label={contactAddress.isFetchingAddresses ? "Fetching addresses..." : selectedRows.size > 0 ? `Get Address (${selectedRows.size})` : "Get Address"}
                    placement="bottom"
                    openDelay={400}
                  >
                    <IconButton
                      aria-label="Get Address"
                      icon={contactAddress.isFetchingAddresses ? <Spinner size="xs" /> : <FiMapPin />}
                      size="xs"
                      variant="ghost"
                      height="28px"
                      color="rgba(255,255,255,0.4)"
                      flexShrink={0}
                      isDisabled={!campaignId || totalContacts === 0 || contactAddress.isFetchingAddresses}
                      _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                      _focus={{ boxShadow: "none" }}
                      onClick={handleFetchAddresses}
                    />
                  </Tooltip>
                  <Tooltip
                    label={emailOrchestration.isVerifying ? "Verifying..." : selectedRows.size > 0 ? `Verify Selected (${selectedRows.size})` : "Verify All Emails"}
                    placement="bottom"
                    openDelay={400}
                  >
                    <IconButton
                      aria-label="Verify Emails"
                      icon={emailOrchestration.isVerifying ? <Spinner size="xs" /> : <CheckCircleIcon />}
                      size="xs"
                      variant="ghost"
                      height="28px"
                      color="rgba(255,255,255,0.4)"
                      flexShrink={0}
                      isDisabled={!campaignId || totalContacts === 0 || emailOrchestration.isVerifying}
                      _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                      _focus={{ boxShadow: "none" }}
                      onClick={handleVerifyEmails}
                    />
                  </Tooltip>
                  <Tooltip label={isExporting ? "Downloading..." : "Download"} placement="bottom" openDelay={400}>
                    <IconButton
                      aria-label="Download"
                      icon={isExporting ? <Spinner size="xs" /> : <DownloadIcon />}
                      size="xs"
                      variant="ghost"
                      height="28px"
                      color="rgba(255,255,255,0.4)"
                      flexShrink={0}
                      isDisabled={!campaignId || isExporting}
                      _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                      _focus={{ boxShadow: "none" }}
                      onClick={() => handleExport(onNoCreditsModalOpen)}
                    />
                  </Tooltip>
                  {!isAnonymous && (
                    <Tooltip
                      label={
                        allMessageDrafts.length === 0
                          ? "Draft at least one message to send for compliance review"
                          : "Send for compliance review"
                      }
                      placement="bottom"
                      openDelay={400}
                    >
                      <IconButton
                        aria-label="Send for compliance review"
                        icon={<ShieldCheck size={14} strokeWidth={1.75} />}
                        size="xs"
                        variant="ghost"
                        height="28px"
                        color="rgba(255,255,255,0.4)"
                        flexShrink={0}
                        isDisabled={
                          !campaignId ||
                          totalContacts === 0 ||
                          allMessageDrafts.length === 0
                        }
                        _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                        _focus={{ boxShadow: "none" }}
                        onClick={onComplianceReviewModalOpen}
                      />
                    </Tooltip>
                  )}
                  <Menu placement="bottom-end">
                    <Tooltip label="Export contacts" placement="bottom" openDelay={400}>
                      <MenuButton
                        as={IconButton}
                        aria-label="Export contacts"
                        icon={<FiSend />}
                        size="xs"
                        variant="ghost"
                        height="28px"
                        color="rgba(255,255,255,0.4)"
                        flexShrink={0}
                        isDisabled={!campaignId || totalContacts === 0}
                        _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                        _focus={{ boxShadow: "none" }}
                      />
                    </Tooltip>
                    <MenuList bg="#0f0f0f" borderColor="rgba(255,255,255,0.10)" minW="0">
                      <Tooltip
                        label={
                          !isCampaignApproved
                            ? "Campaign must be approved before exporting"
                            : !smartleadIntegration.isConnected
                              ? "Connect Smartlead in Integrations to export contacts"
                              : ""
                        }
                        isDisabled={isCampaignApproved && smartleadIntegration.isConnected}
                        placement="left"
                      >
                        <MenuItem
                          icon={<FiSend />}
                          onClick={onSmartleadExportModalOpen}
                          isDisabled={!isCampaignApproved || !smartleadIntegration.isConnected}
                          bg="#0f0f0f"
                          color={isCampaignApproved && smartleadIntegration.isConnected ? "#fafafa" : "rgba(255,255,255,0.3)"}
                          fontSize="sm"
                          _hover={{ bg: isCampaignApproved && smartleadIntegration.isConnected ? "rgba(255,255,255,0.06)" : "#0f0f0f" }}
                          cursor={isCampaignApproved && smartleadIntegration.isConnected ? "pointer" : "not-allowed"}
                        >
                          Smartlead
                        </MenuItem>
                      </Tooltip>
                      <Tooltip
                        label={
                          !isCampaignApproved
                            ? "Campaign must be approved before exporting"
                            : !heyreachIntegration.isConnected
                              ? "Connect HeyReach in Integrations to export contacts"
                              : ""
                        }
                        isDisabled={isCampaignApproved && heyreachIntegration.isConnected}
                        placement="left"
                      >
                        <MenuItem
                          icon={<FiSend />}
                          onClick={onHeyreachExportModalOpen}
                          isDisabled={!isCampaignApproved || !heyreachIntegration.isConnected}
                          bg="#0f0f0f"
                          color={isCampaignApproved && heyreachIntegration.isConnected ? "#fafafa" : "rgba(255,255,255,0.3)"}
                          fontSize="sm"
                          _hover={{ bg: isCampaignApproved && heyreachIntegration.isConnected ? "rgba(255,255,255,0.06)" : "#0f0f0f" }}
                          cursor={isCampaignApproved && heyreachIntegration.isConnected ? "pointer" : "not-allowed"}
                        >
                          HeyReach
                        </MenuItem>
                      </Tooltip>
                    </MenuList>
                  </Menu>
                </>
              )}

              {/* Search bar */}
              <InputGroup
                size="xs"
                minW={{ base: "140px", md: "180px" }}
                maxW={selectedRows.size > 0 ? "220px" : "300px"}
                flexShrink={1}
              >
                <InputLeftElement pointerEvents="none" h="28px">
                  {isLoadingTabData ? (
                    <Spinner size="xs" color="rgba(255,255,255,0.35)" speed="0.7s" />
                  ) : (
                    <SearchIcon color="rgba(255,255,255,0.25)" boxSize={3} />
                  )}
                </InputLeftElement>
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applySearch();
                    }
                  }}
                  placeholder={`Search ${activeTab === 0 ? 'companies' : 'contacts'}...`}
                  h="28px"
                  borderColor="rgba(255,255,255,0.12)"
                  color="#fafafa"
                  bg="transparent"
                  _placeholder={{ color: "rgba(255,255,255,0.25)" }}
                  _hover={{ borderColor: "rgba(255,255,255,0.22)" }}
                  _focus={{ borderColor: "rgba(255,255,255,0.35)", boxShadow: "none" }}
                  pr={searchInput ? "36px" : undefined}
                />
                {searchInput && (
                  <InputRightElement h="28px" w="36px">
                    {searchInput.trim() !== appliedSearch.trim() ? (
                      <Tooltip label="Press Enter to search" placement="top" openDelay={300}>
                        <Box
                          fontSize="9px"
                          color="rgba(255,255,255,0.3)"
                          fontFamily="mono"
                          cursor="default"
                          userSelect="none"
                          border="1px solid"
                          borderColor="rgba(255,255,255,0.15)"
                          borderRadius="sm"
                          px="3px"
                          lineHeight="16px"
                          flexShrink={0}
                        >
                          ↵
                        </Box>
                      </Tooltip>
                    ) : (
                      <IconButton
                        aria-label="Clear search"
                        icon={<CloseIcon boxSize={2} />}
                        size="xs"
                        variant="ghost"
                        color="rgba(255,255,255,0.35)"
                        _hover={{ bg: "rgba(255,255,255,0.06)", color: "#fafafa" }}
                        _focus={{ boxShadow: "none" }}
                        onClick={() => {
                          setSearchInput('');
                          setAppliedSearch('');
                          fetchTabData(activeTab, 0, false, '');
                        }}
                      />
                    )}
                  </InputRightElement>
                )}
              </InputGroup>
              {selectedRows.size > 0 && !isLocked && (
                <Button
                  leftIcon={<DeleteIcon />}
                  size="xs"
                  variant="solid"
                  height="28px"
                  fontSize="xs"
                  flexShrink={0}
                  bg="rgba(239,68,68,0.15)"
                  color="rgba(239,68,68,0.85)"
                  border="1px solid rgba(239,68,68,0.25)"
                  borderRadius="6px"
                  _hover={{ bg: "rgba(239,68,68,0.25)", borderColor: "rgba(239,68,68,0.4)" }}
                  _focus={{ boxShadow: "none" }}
                  onClick={handleDeleteSelectedRows}
                >
                  Delete Selected ({selectedRows.size})
                </Button>
              )}
          </HStack>
          )}
          {selectedCompany && (
            <HStack spacing={2}>
              <Button
                size="xs"
                variant="outline"
                height="28px"
                px={3}
                fontSize="xs"
                fontWeight="500"
                borderColor="rgba(255,255,255,0.12)"
                color="#a3a3a3"
                leftIcon={(isBulkResearching || isResearchRunning) ? <Spinner size="xs" /> : <Play size={11} strokeWidth={2.5} />}
                isDisabled={researchFields.length === 0 || isBulkResearching || isResearchRunning}
                _hover={researchFields.length > 0 && !(isBulkResearching || isResearchRunning) ? {
                  borderColor: 'rgba(255,255,255,0.25)',
                  color: '#fafafa',
                  bg: 'rgba(255,255,255,0.04)',
                } : {}}
                _disabled={{ opacity: 0.35, cursor: 'not-allowed' }}
                _active={{ bg: 'rgba(255,255,255,0.06)' }}
                _focus={{ boxShadow: 'none' }}
                transition="all 0.15s ease"
                onClick={() => {
                  if (researchFields.length === 0 || isBulkResearching || isResearchRunning) return;
                  const selections = {};
                  researchFields.forEach(f => {
                    if (f.config_id) selections[f.config_id] = true;
                  });
                  setRunConfigSelections(selections);
                  setIsRunModalOpen(true);
                }}
              >
                {(isBulkResearching || isResearchRunning) ? 'Running...' : 'Run for all'}
              </Button>
            </HStack>
          )}
          {selectedContact && (
            <HStack spacing={2}>
              <Button
                size="xs"
                variant="outline"
                height="28px"
                px={3}
                fontSize="xs"
                fontWeight="500"
                borderColor="rgba(255,255,255,0.12)"
                color="#a3a3a3"
                leftIcon={(isBulkResearching || isResearchRunning) ? <Spinner size="xs" /> : <Play size={11} strokeWidth={2.5} />}
                isDisabled={contactResearchFields.length === 0 || isBulkResearching || isResearchRunning}
                _hover={contactResearchFields.length > 0 && !(isBulkResearching || isResearchRunning) ? {
                  borderColor: 'rgba(255,255,255,0.25)',
                  color: '#fafafa',
                  bg: 'rgba(255,255,255,0.04)',
                } : {}}
                _disabled={{ opacity: 0.35, cursor: 'not-allowed' }}
                _active={{ bg: 'rgba(255,255,255,0.06)' }}
                _focus={{ boxShadow: 'none' }}
                transition="all 0.15s ease"
                onClick={() => {
                  if (contactResearchFields.length === 0 || isBulkResearching || isResearchRunning) return;
                  const selections = {};
                  contactResearchFields.forEach(f => {
                    if (f.config_id) selections[f.config_id] = true;
                  });
                  setContactRunConfigSelections(selections);
                  setIsContactRunModalOpen(true);
                }}
              >
                {(isBulkResearching || isResearchRunning) ? 'Running...' : 'Run for all'}
              </Button>
            </HStack>
          )}
        </HStack>
      </Box>

      {/* Campaign-wide compliance status banner */}
      {!isAnonymous && !selectedCompany && !selectedContact && (() => {
        const showPending = complianceStatus === 'pending' || (!complianceStatus && hasComplianceReviewers);
        if (!complianceStatus && !showPending) return null;

        const STATE = {
          approved: { label: 'Approved',       badgeBg: 'rgba(34,197,94,0.12)',  badgeBorder: 'rgba(34,197,94,0.30)',  color: 'rgba(74,222,128,0.9)',  stripBorder: 'rgba(34,197,94,0.20)',  stripBg: 'rgba(34,197,94,0.05)',  body: 'Your reviewer approved this campaign' },
          rejected: { label: 'Rejected',       badgeBg: 'rgba(239,68,68,0.12)',  badgeBorder: 'rgba(239,68,68,0.30)',  color: 'rgba(248,113,113,0.9)', stripBorder: 'rgba(239,68,68,0.20)',  stripBg: 'rgba(239,68,68,0.05)',  body: 'Your reviewer rejected this campaign — revise and re-invite to reset' },
          pending:  { label: 'Pending Review', badgeBg: 'rgba(251,191,36,0.10)', badgeBorder: 'rgba(251,191,36,0.28)', color: 'rgba(251,191,36,0.9)',  stripBorder: 'rgba(251,191,36,0.15)', stripBg: 'rgba(251,191,36,0.03)', body: 'Waiting for your reviewer to approve or reject' },
        };

        const key = complianceStatus === 'approved' ? 'approved' : complianceStatus === 'rejected' ? 'rejected' : 'pending';
        const s = STATE[key];

        return (
          <Box flexShrink={0} px={4} py={2} borderBottom="1px solid" borderColor={s.stripBorder} bg={s.stripBg}>
            <HStack spacing={2} align="center">
              <Box px={2} py="2px" borderRadius="full" bg={s.badgeBg} border={`1px solid ${s.badgeBorder}`} flexShrink={0}>
                <Text fontSize="10px" fontWeight="700" color={s.color} letterSpacing="0.06em" textTransform="uppercase">
                  {s.label}
                </Text>
              </Box>
              <Text fontSize="xs" color="rgba(255,255,255,0.55)" flex={1} noOfLines={1}>{s.body}</Text>
            </HStack>
          </Box>
        );
      })()}

      {/* Tabs */}
      <Tabs 
        index={activeTab} 
        onChange={handleTabChange} 
        variant="enclosed" 
        colorScheme="blue"
        display="flex"
        flexDirection="column"
        flex={1}
        minH={0}
      >
        <TabList borderBottom="1px solid" borderColor="rgba(255,255,255,0.08)" bg="#0a0a0a" mt={4} minH={0} py={0} gap={0} display={(selectedCompany || selectedContact) ? 'none' : 'flex'}>
          <Tab
            color="rgba(255,255,255,0.35)"
            fontSize="xs"
            py={1.5}
            px={3}
            _selected={{ color: "#fafafa", borderColor: "rgba(255,255,255,0.25)", bg: "rgba(255,255,255,0.05)" }}
            _hover={{ color: "rgba(255,255,255,0.7)" }}
            _focus={{ boxShadow: "none" }}
          >
            Companies ({totalCompanies})
          </Tab>
          <Tab
            color="rgba(255,255,255,0.35)"
            fontSize="xs"
            py={1.5}
            px={3}
            _selected={{ color: "#fafafa", borderColor: "rgba(255,255,255,0.25)", bg: "rgba(255,255,255,0.05)" }}
            _hover={{ color: "rgba(255,255,255,0.7)" }}
            _focus={{ boxShadow: "none" }}
          >
            Contacts ({totalContacts})
          </Tab>
        </TabList>
        
        <TabPanels flex={1} minH={0} h="100%">
          {/* Companies Tab */}
          <TabPanel p={0} h="100%" display="flex" flexDirection="column">
            {selectedCompany ? (
              /* Notion-style company detail view */
              <CompanyDetailView
                company={selectedCompany}
                onBack={() => setSelectedCompany(null)}
                customFields={companyFields.fieldDefinitions}
                onFieldsChange={setResearchFields}
                campaignId={campaignId}
                onPrev={handleCompanyPrev}
                onNext={handleCompanyNext}
                currentIndex={selectedCompanyIndex}
                totalCount={totalCompanies}
              />
            ) : (
              <>
                {companyFields.previewConfigId && !isGeneratingCustomFields && (
                  <CustomFieldPreviewBanner
                    previewCount={companyFields.previewMembershipIds.size || companiesData.length}
                    entityType="companies"
                    onApprove={handleApprovePreview}
                    onReject={handleRejectPreview}
                  />
                )}
                <Box ref={companiesOuterContainerRef} flex={1} minH={0} overflowX="auto" overflowY="hidden" position="relative">
                  {/* Width measurement container - always rendered */}
                  <Box ref={widthMeasureRef} position="absolute" top={0} left={0} right={0} h={0} />
                  {isLoadingTabData && companiesData.length === 0 ? (
                    <Box 
                      h="100%" 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center"
                    >
                      <Spinner
                        size="xl"
                        thickness="4px"
                        speed="0.65s"
                        color="rgba(255,255,255,0.3)"
                      />
                    </Box>
                  ) : (
                    <>
                      <CompanyTableHeader
                        columns={COMPANY_COLUMNS}
                        selectedCount={selectedRows.size}
                        totalCount={totalCompanies}
                        onSelectAll={handleSelectAllRows}
                        isSelectingAll={isSelectingAllRows}
                        fieldDefinitions={companyFields.fieldDefinitions}
                        minWidth={COMPANY_TABLE_MIN_WIDTH}
                        onContinueGeneration={handleContinueGeneration}
                        isStatusBarVisible={isStatusBarVisible}
                        onDeleteField={isLocked ? undefined : handleDeleteCompanyField}
                      />
                      
                      <Box minW={`${COMPANY_TABLE_MIN_WIDTH}px`} w="100%">
                        <TableContainer
                          headerComponent={null}
                          data={listData}
                          height={listHeight}
                          onScroll={handleScroll}
                          itemSize={getItemSize}
                          minWidth={COMPANY_TABLE_MIN_WIDTH}
                          rowComponent={TableRow}
                          listRef={listRef}
                          containerRef={containerRef}
                          listKey={`companies-${companyFields.fieldDefinitions.length}-completion-${customFieldCompletionCounter}`}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              </>
            )}
          </TabPanel>
          
          {/* Contacts Tab */}
          <TabPanel p={0} h="100%" display="flex" flexDirection="column">
            {selectedContact ? (
              /* Notion-style contact detail view */
              <ContactDetailView
                contact={selectedContact}
                onBack={() => setSelectedContact(null)}
                customFields={contactFields.fieldDefinitions}
                onFieldsChange={setContactResearchFields}
                campaignId={campaignId}
                contactedFieldId={contactedFieldId}
                notesFieldId={notesFieldId}
                onContactedChange={handleContactedChange}
                onNotesChange={handleNotesChange}
                companiesData={companiesData}
                onPrev={handleContactPrev}
                onNext={handleContactNext}
                currentIndex={selectedContactIndex}
                totalCount={totalContacts}
              />
            ) : (
              <>
                {contactFields.previewConfigId && !isGeneratingCustomFields && (
                  <CustomFieldPreviewBanner
                    previewCount={contactFields.previewMembershipIds.size || contactsData.length}
                    entityType="contacts"
                    onApprove={handleApprovePreview}
                    onReject={handleRejectPreview}
                  />
                )}
                {/* Phase 3.5 — aggregate compliance overview, renders only when
                    the campaign actually has accepted reviewers and at least
                    one reviewable (contact, draft) pair exists. */}
                <AdvisorComplianceSummaryStrip
                  isEnabled={advisorCompliance.isEnabled}
                  summary={mergedComplianceSummary}
                  filter={advisorCompliance.filter}
                  onFilterChange={handleAdvisorComplianceFilterChange}
                  // Plan A — chip numbers come from `/compliance-summary`,
                  // not from "have we loaded every row yet?". Spin only
                  // while the summary query has no value at all (first
                  // paint or disabled→enabled flip); refetches reuse the
                  // last value so chips never blank between updates.
                  isCountLoading={
                    advisorCompliance.isEnabled
                    && isComplianceSummaryPending
                    && !backendComplianceSummary
                  }
                />
                <Box ref={contactsOuterContainerRef} flex={1} minH={0} overflowX="auto" overflowY="hidden" position="relative">
                  {/* Width measurement container - always rendered */}
                  <Box ref={widthMeasureRef} position="absolute" top={0} left={0} right={0} h={0} />
                  {isLoadingTabData && contactsData.length === 0 ? (
                    <Box 
                      h="100%" 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center"
                    >
                      <Spinner
                        size="xl"
                        thickness="4px"
                        speed="0.65s"
                        color="rgba(255,255,255,0.3)"
                      />
                    </Box>
                  ) : (
                    <>
                      <ContactTableHeader
                        columns={CONTACT_COLUMNS}
                        selectedCount={selectedRows.size}
                        totalCount={totalContacts}
                        onSelectAll={handleSelectAllRows}
                        isSelectingAll={isSelectingAllRows}
                        fieldDefinitions={renderableContactFieldDefs}
                        minWidth={CONTACT_TABLE_MIN_WIDTH}
                        onContinueGeneration={handleContinueGeneration}
                        isStatusBarVisible={isStatusBarVisible}
                        messageDrafts={allMessageDrafts}
                        exportTracking={exportTracking}
                        exportedGroupIdsSmartlead={exportedGroupIdsSmartlead}
                        exportedGroupIdsHeyreach={exportedGroupIdsHeyreach}
                        exportedGroupIdsGillyreach={exportedGroupIdsGillyreach}
                        collapsedExportGroupIds={collapsedExportGroupIds}
                        onToggleExportGroup={(groupId) => {
                          setCollapsedExportGroupIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(groupId)) next.delete(groupId);
                            else next.add(groupId);
                            return next;
                          });
                        }}
                        onMessageDraftEdit={onMessageDraftEdit}
                        onMessageDraftRename={onMessageDraftRename}
                        onMessageDraftDelete={isLocked ? undefined : handleMessageDraftDeleteWithFieldRefresh}
                        onMessageDraftDescriptionUpdate={onMessageDraftDescriptionUpdate}
                        onMessageDraftContinueGeneration={handleMessageDraftContinueGeneration}
                        generatingDraftId={generatingDraftId}
                        onDeleteField={isLocked ? undefined : handleDeleteContactField}
                      />
                      
                      <Box
                        minW={`${CONTACT_TABLE_MIN_WIDTH}px`}
                        w="100%"
                        transition="min-width 260ms cubic-bezier(0.22, 1, 0.36, 1)"
                      >
                        <TableContainer
                          headerComponent={null}
                          data={listData}
                          height={listHeight}
                          onScroll={handleScroll}
                          itemSize={getItemSize}
                          minWidth={CONTACT_TABLE_MIN_WIDTH}
                          rowComponent={TableRow}
                          listRef={listRef}
                          containerRef={containerRef}
                          listKey={`contacts-${contactFields.fieldDefinitions.length}-completion-${customFieldCompletionCounter}`}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              </>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Phase 3.5 — single shared modal for reviewer feedback on
          non-compliant drafts. Controlled entirely via advisorCompliance
          state so any chip click in the virtualized table can open it
          without state plumbed through every row. */}
      <AdvisorNonCompliantModal
        context={advisorCompliance.modalContext}
        onClose={advisorCompliance.closeNonCompliantModal}
      />

      {/* No Credits Modal */}
      <Modal isOpen={isNoCreditsModalOpen} onClose={onNoCreditsModalClose} isCentered>
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#0a0a0a" color="#fafafa" border="1px solid rgba(255,255,255,0.10)" borderRadius="12px">
          <ModalHeader fontSize="lg" fontWeight="700" letterSpacing="-0.01em" pb={3} borderBottomWidth="1px" borderColor="rgba(255,255,255,0.08)">
            Insufficient Credits
          </ModalHeader>
          <ModalCloseButton color="#a3a3a3" _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }} _focus={{ boxShadow: 'none' }} />
          <ModalBody pb={6}>
            <VStack spacing={3} align="stretch">
              <Text fontSize="sm" color="#a3a3a3">
                You don't have enough credits for this operation. Purchase more credits or subscribe to Pro for unlimited access.
              </Text>
              <Box bg="rgba(255,255,255,0.02)" p={4} borderRadius="10px" border="1px solid rgba(255,255,255,0.08)">
                <VStack spacing={1.5} align="start">
                  <Text fontSize="sm" fontWeight="600" color="#fafafa">🪙 Credits — $25 per 1,000</Text>
                  <Text fontSize="sm" color="#a3a3a3">
                    Visit the billing page to purchase credits or subscribe to Pro for 10,000 monthly credits.
                  </Text>
                </VStack>
              </Box>
              <Box bg="rgba(255,255,255,0.02)" p={4} borderRadius="10px" border="1px solid rgba(255,255,255,0.08)">
                <VStack spacing={1.5} align="start">
                  <Text fontSize="sm" fontWeight="600" color="#fafafa">🚀 Pro Subscription — $100/month</Text>
                  <Text fontSize="sm" color="#a3a3a3">
                    Get unlimited exports, unlimited data, and priority support with a Pro subscription.
                  </Text>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="flex-end" gap={2} borderTopWidth="1px" borderColor="rgba(255,255,255,0.08)">
            <Button
              variant="ghost"
              color="#a3a3a3"
              borderRadius="full"
              fontSize="sm"
              _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
              _focus={{ boxShadow: 'none' }}
              onClick={() => {
                onNoCreditsModalClose();
                window.location.href = '/billing';
              }}
            >
              Buy Credits
            </Button>
            <Button
              bg="#fafafa"
              color="#0a0a0a"
              borderRadius="full"
              fontWeight="600"
              fontSize="sm"
              _hover={{ opacity: 0.85 }}
              _focus={{ boxShadow: 'none' }}
              onClick={() => {
                onNoCreditsModalClose();
                window.location.href = '/billing';
              }}
            >
              Go to Billing
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Custom Field Generation Modal */}
      <CustomFieldGenerationModal
        isOpen={isCustomFieldModalOpen}
        onClose={onCustomFieldModalClose}
        campaignId={campaignId}
        entityType={activeTab === 0 ? 'companies' : 'contacts'}
        onGenerate={handleGenerateCustomFields}
      />

      {/* Email Draft Modal */}
      <EmailDraftModal
        isOpen={isEmailDraftModalOpen}
        onClose={onEmailDraftModalClose}
        onGenerate={handleGenerateEmailDrafts}
      />

      {/* Smartlead Export Modal */}
      <SmartleadExportModal
        isOpen={isSmartleadExportModalOpen}
        onClose={onSmartleadExportModalClose}
        campaignId={campaignId}
        versionId={activeVersionId}
        onExportComplete={async () => {
          // Refresh table data + export tracking in parallel
          await Promise.all([
            fetchTabData(activeTab, 0, false),
            onExportTrackingRefresh?.(),
          ]);
          
          // Scroll to top after refresh
          setTimeout(() => {
            listRef.current?.scrollToItem?.(0, 'start');
          }, 0);
          
          // Dispatch event to refresh credit count in header
          window.dispatchEvent(new Event('exportCompleted'));
        }}
      />

      {/* Compliance Review Modal */}
      <ComplianceReviewModal
        isOpen={isComplianceReviewModalOpen}
        onClose={onComplianceReviewModalClose}
        campaignId={campaignId}
      />

      {/* HeyReach Export Modal */}
      <HeyreachExportModal
        isOpen={isHeyreachExportModalOpen}
        onClose={onHeyreachExportModalClose}
        campaignId={campaignId}
        versionId={activeVersionId}
        onExportComplete={async () => {
          // Refresh table data + export tracking in parallel
          await Promise.all([
            fetchTabData(activeTab, 0, false),
            onExportTrackingRefresh?.(),
          ]);
          
          // Scroll to top after refresh
          setTimeout(() => {
            listRef.current?.scrollToItem?.(0, 'start');
          }, 0);
          
          // Dispatch event to refresh credit count in header
          window.dispatchEvent(new Event('exportCompleted'));
        }}
      />

      {/* Credit Warning Modal */}
      {warningData && (
        <CreditWarningModal
          isOpen={isWarningOpen}
          onClose={closeWarning}
          operationType={warningData.operation_type}
          creditsNeeded={warningData.credits_needed}
          currentBalance={warningData.balance}
          onConfirm={handleConfirm}
          onOptOut={handleOptOut}
          userSubscriptionStatus={userSubscriptionStatus}
          allowsOverageProceed={warningData.preview_allows_overage_proceed === true}
          overageCreditsForRequest={warningData.overage_credits_for_request}
          overagePricePerCreditCents={warningData.overage_price_per_credit_cents}
        />
      )}

      {/* Run for All — Config selection modal */}
      {(() => {
        // Group fields by config_id
        const configGroups = {};
        researchFields.forEach(f => {
          const cid = f.config_id;
          if (!cid) return;
          if (!configGroups[cid]) configGroups[cid] = [];
          configGroups[cid].push(f);
        });
        const groupEntries = Object.entries(configGroups);
        const selectedCount = Object.values(runConfigSelections).filter(Boolean).length;

        return (
          <Modal
            isOpen={isRunModalOpen}
            onClose={() => setIsRunModalOpen(false)}
            isCentered
            size="md"
          >
            <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
            <ModalContent
              bg="#111111"
              borderColor="rgba(255,255,255,0.10)"
              borderWidth="1px"
              borderRadius="10px"
              overflow="hidden"
            >
              <ModalHeader pt={5} pb={0} px={6} fontSize="md" fontWeight="600" letterSpacing="-0.01em" color="#fafafa">
                <HStack spacing={2.5}>
                  <Box p={1.5} borderRadius="6px" bg="rgba(255,255,255,0.06)">
                    <Play size={14} color="rgba(96,165,250,0.9)" strokeWidth={2.5} />
                  </Box>
                  <Text>Run for all companies</Text>
                </HStack>
                <Text fontSize="xs" fontWeight="400" color="#a3a3a3" mt={1.5}>
                  Choose which research fields to run across every company
                </Text>
              </ModalHeader>
              <ModalCloseButton color="#a3a3a3" top={4} right={4} _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)', borderRadius: 'full' }} />

              <ModalBody px={6} py={4}>
                {groupEntries.length === 0 ? (
                  <Text color="#a3a3a3" fontSize="sm" textAlign="center" py={6}>
                    No research fields yet
                  </Text>
                ) : (
                  <VStack spacing={1} align="stretch">
                    {groupEntries.map(([configId, fields]) => {
                      const isSelected = runConfigSelections[configId] || false;
                      const isBundle = fields.length > 1;

                      return (
                        <Box key={configId}>
                          <Box
                            px={3} py={2.5}
                            borderRadius="8px"
                            cursor="pointer"
                            transition="all 0.12s ease"
                            bg={isSelected ? 'rgba(255,255,255,0.05)' : 'transparent'}
                            _hover={{ bg: 'rgba(255,255,255,0.05)' }}
                            onClick={() => {
                              setRunConfigSelections(prev => ({
                                ...prev,
                                [configId]: !prev[configId],
                              }));
                            }}
                          >
                            <HStack spacing={3} align="center">
                              {/* Checkbox */}
                              <Box
                                w="18px" h="18px" flexShrink={0}
                                borderRadius="5px"
                                border="1.5px solid"
                                borderColor={isSelected ? 'rgba(96,165,250,0.8)' : 'rgba(255,255,255,0.2)'}
                                bg={isSelected ? 'rgba(59,130,246,0.85)' : 'transparent'}
                                display="flex" alignItems="center" justifyContent="center"
                                transition="all 0.12s ease"
                              >
                                {isSelected && <Check size={11} color="white" strokeWidth={3} />}
                              </Box>

                              {/* Label area */}
                              <Box flex={1} minW={0}>
                                {isBundle ? (
                                  <>
                                    <HStack spacing={1.5} mb={0.5}>
                                      <Sparkles size={11} color="rgba(96,165,250,0.9)" strokeWidth={2.5} />
                                      <Text fontSize="xs" fontWeight="600" color="rgba(96,165,250,0.9)" letterSpacing="0.02em">
                                        Deep Dive
                                      </Text>
                                      <Text fontSize="xs" color="rgba(255,255,255,0.35)" fontWeight="400">
                                        {fields.length} fields
                                      </Text>
                                    </HStack>
                                    <VStack spacing={0} align="stretch" pl={0.5}>
                                      {fields.map(f => (
                                        <Text key={f.id} fontSize="sm" fontWeight="400" color="#a3a3a3" noOfLines={1}>
                                          {f.label}
                                        </Text>
                                      ))}
                                    </VStack>
                                  </>
                                ) : (
                                  <Text fontSize="sm" fontWeight="500" color="rgba(255,255,255,0.85)" noOfLines={1}>
                                    {fields[0].label}
                                  </Text>
                                )}
                              </Box>
                            </HStack>
                          </Box>
                        </Box>
                      );
                    })}
                  </VStack>
                )}
              </ModalBody>

              <Box px={6} pb={5} pt={2} borderTop="1px solid rgba(255,255,255,0.08)">
                <HStack spacing={3} justify="space-between">
                  <Text fontSize="xs" color="rgba(255,255,255,0.35)" fontWeight="400">
                    {selectedCount} of {groupEntries.length} selected
                  </Text>
                  <HStack spacing={3}>
                    <Button
                      size="sm" height="32px" px={4}
                      fontSize="xs" fontWeight="500"
                      bg="transparent"
                      border="1px solid rgba(255,255,255,0.15)"
                      color="rgba(255,255,255,0.6)"
                      borderRadius="full"
                      _hover={{ borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.9)', bg: 'rgba(255,255,255,0.04)' }}
                      onClick={() => setIsRunModalOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm" height="32px" px={4}
                      fontSize="xs" fontWeight="600"
                      bg="#fafafa" color="#0a0a0a" borderRadius="full"
                      isDisabled={selectedCount === 0 || isBulkResearching || isResearchRunning}
                      leftIcon={(isBulkResearching || isResearchRunning) ? <Spinner size="xs" color="#0a0a0a" /> : <Play size={12} strokeWidth={2.5} />}
                      _hover={{ opacity: 0.85 }}
                      _disabled={{ opacity: 0.3, cursor: 'not-allowed' }}
                      transition="all 0.15s ease"
                      onClick={() => {
                        const selectedConfigIds = Object.entries(runConfigSelections)
                          .filter(([, v]) => v)
                          .map(([cid]) => cid);
                        if (selectedConfigIds.length === 0) return;

                        setIsRunModalOpen(false);

                        void withCreditWarning(
                          'run_for_all',
                          {
                            campaign_id: campaignId,
                            version_id: activeVersionId,
                            config_ids: selectedConfigIds,
                          },
                          () => {
                            toast({
                              title: 'Research started',
                              description: 'Running across all companies',
                              status: 'info',
                              duration: 4000,
                              isClosable: true,
                            });
                            // Show status bar immediately so the user sees progress
                            // in the chat panel before the first SSE status event arrives.
                            onResearchStatusUpdateRef.current?.(
                              { completion_percentage: '1', message: 'Starting research...', operation_id: 'custom_field_generation' },
                              'custom_fields',
                            );
                            startResearchStream(
                              `/api/research/campaigns/${campaignId}/run-for-all`,
                              { method: 'POST', body: { config_ids: selectedConfigIds } },
                            );
                          },
                        ).catch((e) => {
                          if (!e?.isCancellation) console.error('[Run selected companies]', e);
                        });
                      }}
                    >
                      {(isBulkResearching || isResearchRunning) ? 'Starting...' : 'Run selected'}
                    </Button>
                  </HStack>
                </HStack>
              </Box>
            </ModalContent>
          </Modal>
        );
      })()}


      {/* Run for All — Contacts */}
      {(() => {
        const configGroups = {};
        contactResearchFields.forEach(f => {
          const cid = f.config_id;
          if (!cid) return;
          if (!configGroups[cid]) configGroups[cid] = [];
          configGroups[cid].push(f);
        });
        const groupEntries = Object.entries(configGroups);
        const selectedCount = Object.values(contactRunConfigSelections).filter(Boolean).length;

        return (
          <Modal isOpen={isContactRunModalOpen} onClose={() => setIsContactRunModalOpen(false)} isCentered size="md">
            <ModalOverlay bg="rgba(0,0,0,0.85)" />
            <ModalContent bg="#111111" borderColor="rgba(255,255,255,0.10)" borderWidth="1px" borderRadius="10px" overflow="hidden">
              <ModalHeader pt={5} pb={0} px={6} fontSize="md" fontWeight="600" letterSpacing="-0.01em" color="#fafafa">
                <HStack spacing={2.5}>
                  <Box p={1.5} borderRadius="6px" bg="rgba(255,255,255,0.06)">
                    <Play size={14} color="rgba(96,165,250,0.9)" strokeWidth={2.5} />
                  </Box>
                  <Text>Run for all contacts</Text>
                </HStack>
                <Text fontSize="xs" fontWeight="400" color="#a3a3a3" mt={1.5}>
                  Choose which research fields to run across every contact
                </Text>
              </ModalHeader>
              <ModalCloseButton color="#a3a3a3" top={4} right={4} _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)', borderRadius: 'full' }} />
              <ModalBody px={6} py={4}>
                {groupEntries.length === 0 ? (
                  <Text color="#a3a3a3" fontSize="sm" textAlign="center" py={6}>No research fields yet</Text>
                ) : (
                  <VStack spacing={1} align="stretch">
                    {groupEntries.map(([configId, fields]) => {
                      const isSelected = contactRunConfigSelections[configId] || false;
                      const isBundle = fields.length > 1;
                      return (
                        <Box key={configId}>
                          <Box px={3} py={2.5} borderRadius="8px" cursor="pointer" transition="all 0.12s ease"
                            bg={isSelected ? 'rgba(255,255,255,0.05)' : 'transparent'} _hover={{ bg: 'rgba(255,255,255,0.05)' }}
                            onClick={() => setContactRunConfigSelections(prev => ({ ...prev, [configId]: !prev[configId] }))}>
                            <HStack spacing={3} align="center">
                              <Box w="18px" h="18px" flexShrink={0} borderRadius="5px" border="1.5px solid"
                                borderColor={isSelected ? 'rgba(96,165,250,0.8)' : 'rgba(255,255,255,0.2)'}
                                bg={isSelected ? 'rgba(59,130,246,0.85)' : 'transparent'}
                                display="flex" alignItems="center" justifyContent="center" transition="all 0.12s ease">
                                {isSelected && <Check size={11} color="white" strokeWidth={3} />}
                              </Box>
                              <Box flex={1} minW={0}>
                                {isBundle ? (
                                  <>
                                    <HStack spacing={1.5} mb={0.5}>
                                      <Sparkles size={11} color="rgba(96,165,250,0.9)" strokeWidth={2.5} />
                                      <Text fontSize="xs" fontWeight="600" color="rgba(96,165,250,0.9)">Deep Dive</Text>
                                      <Text fontSize="xs" color="rgba(255,255,255,0.35)">{fields.length} fields</Text>
                                    </HStack>
                                    <VStack spacing={0} align="stretch">
                                      {fields.map(f => (
                                        <Text key={f.id} fontSize="sm" fontWeight="400" color="#a3a3a3" noOfLines={1}>{f.label}</Text>
                                      ))}
                                    </VStack>
                                  </>
                                ) : (
                                  <Text fontSize="sm" fontWeight="500" color="rgba(255,255,255,0.85)" noOfLines={1}>{fields[0].label}</Text>
                                )}
                              </Box>
                            </HStack>
                          </Box>
                        </Box>
                      );
                    })}
                  </VStack>
                )}
              </ModalBody>
              <Box px={6} pb={5} pt={2} borderTop="1px solid rgba(255,255,255,0.08)">
                <HStack spacing={3} justify="space-between">
                  <Text fontSize="xs" color="rgba(255,255,255,0.35)">{selectedCount} of {groupEntries.length} selected</Text>
                  <HStack spacing={3}>
                    <Button size="sm" height="32px" px={4} fontSize="xs" fontWeight="500"
                      bg="transparent"
                      border="1px solid rgba(255,255,255,0.15)"
                      color="rgba(255,255,255,0.6)"
                      borderRadius="full"
                      _hover={{ borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.9)', bg: 'rgba(255,255,255,0.04)' }}
                      onClick={() => setIsContactRunModalOpen(false)}>
                      Cancel
                    </Button>
                    <Button size="sm" height="32px" px={4} fontSize="xs" fontWeight="600"
                      bg="#fafafa" color="#0a0a0a" borderRadius="full"
                      isDisabled={selectedCount === 0 || isBulkResearching || isResearchRunning}
                      leftIcon={(isBulkResearching || isResearchRunning) ? <Spinner size="xs" color="#0a0a0a" /> : <Play size={12} strokeWidth={2.5} />}
                      _hover={{ opacity: 0.85 }}
                      _disabled={{ opacity: 0.3, cursor: 'not-allowed' }} transition="all 0.15s ease"
                      onClick={() => {
                        const selectedConfigIds = Object.entries(contactRunConfigSelections)
                          .filter(([, v]) => v).map(([cid]) => cid);
                        if (selectedConfigIds.length === 0) return;
                        setIsContactRunModalOpen(false);
                        void withCreditWarning(
                          'run_for_all',
                          { campaign_id: campaignId, version_id: activeVersionId, config_ids: selectedConfigIds, entity_type: 'contact' },
                          () => {
                            toast({ title: 'Research started', description: 'Running across all contacts', status: 'info', duration: 4000, isClosable: true });
                            onResearchStatusUpdateRef.current?.({ completion_percentage: '1', message: 'Starting research...', operation_id: 'custom_field_generation' }, 'custom_fields');
                            startResearchStream(
                              `/api/research/campaigns/${campaignId}/run-for-all`,
                              { method: 'POST', body: { config_ids: selectedConfigIds, entity_type: 'contact' } },
                            );
                          },
                        ).catch((e) => {
                          if (!e?.isCancellation) console.error('[Run selected contacts]', e);
                        });
                      }}>
                      {(isBulkResearching || isResearchRunning) ? 'Starting...' : 'Run selected'}
                    </Button>
                  </HStack>
                </HStack>
              </Box>
            </ModalContent>
          </Modal>
        );
      })()}

    </Box>
  );
};

export default React.memo(VirtualizedPersonaResults);

